import { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  IconButton,
  Divider,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  School,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Cancel,
  Quiz,
  VideoLibrary,
  Article,
  Analytics,
  Campaign,
  PlayCircle,
  Pause,
  Edit,
  MoreVert,
  Download,
  Refresh,
  Search,
  Add,
  Group,
  Star,
  Assignment,
  ExpandMore,
  Psychology,
  Lightbulb,
  Timeline,
  AutoGraph,
  Science,
  CarRepair,
  Warning,
  OpenInNew,
  MenuBook,
  VerifiedUser as VerificationIcon,
  Policy,
  NotificationsActive,
  Visibility,
  ContentCopy,
  Archive,
  Delete,
  FileDownload,
  Schedule,
  CalendarMonth,
  Build,
  EventAvailable,
  Description,
  Speed,
  PersonAdd,
  ContactPhone,
  DirectionsCar,
  Payments,
  SupportAgent,
  Security,
  SaveAlt,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

interface NudgeMetric {
  id: string;
  title: string;
  value: number;
  change: number;
  changeDirection: 'up' | 'down';
  icon: React.ReactNode;
  color: string;
  description: string;
}

interface NudgeCampaign {
  id: string;
  name: string;
  type: 'onboarding' | 'performance' | 'engagement' | 'compliance' | 'feature';
  status: 'active' | 'paused' | 'draft' | 'completed';
  targetAudience: string;
  sendMethod: 'email' | 'sms' | 'push' | 'in-app';
  startDate: string;
  endDate?: string;
  delivered: number;
  opened: number;
  clicked: number;
  converted: number;
  segments: string[];
  abTestVariant?: 'A' | 'B';
}

interface HostSegment {
  id: string;
  name: string;
  criteria: string[];
  hostCount: number;
  avgPerformance: number;
  color: string;
  description: string;
}

interface EducationContent {
  id: string;
  title: string;
  type: 'video' | 'article' | 'quiz' | 'checklist' | 'webinar';
  category: 'onboarding' | 'optimization' | 'safety' | 'compliance' | 'growth';
  duration: string;
  completionRate: number;
  rating: number;
  views: number;
  createdAt: string;
  status: 'published' | 'draft' | 'archived';
}

interface NudgeTemplate {
  id: string;
  name: string;
  category: string;
  trigger: string;
  title: string;
  message: string;
  cta: string;
  effectiveness: number;
  usageCount: number;
  lastUsed: string;
}

interface HostNudgeHistory {
  id: string;
  hostId: string;
  hostName: string;
  hostType: string;
  nudgeType: string;
  title: string;
  sentAt: string;
  opened: boolean;
  clicked: boolean;
  converted: boolean;
  score: number;
}

interface TriggeredTip {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  severity: 'warning' | 'info' | 'success';
  triggerCondition: string;
  autoEnabled: boolean;
  impact: string;
  currentTriggers: number;
  lastTriggered: string;
  messageContent: string;
  cta: string;
}

interface DocumentationItem {
  id: string;
  title: string;
  description: string;
  category: 'guide' | 'requirement' | 'policy';
  icon: React.ReactNode;
  url?: string;
  isExternal: boolean;
  lastUpdated: string;
}

interface VideoGuide {
  id: string;
  title: string;
  description: string;
  duration: string;
  thumbnail: string;
  category: string;
  views: number;
  rating: number;
}

interface TrainingModule {
  id: string;
  title: string;
  description: string;
  duration: string;
  completionRate: number;
  category: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
}

interface NudgeEffectivenessMetric {
  id: string;
  title: string;
  value: number;
  change: number;
  changeDirection: 'up' | 'down';
  description: string;
}

const HostEducationNudgesPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [timeRange, setTimeRange] = useState('30d');
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [abTestDialogOpen, setAbTestDialogOpen] = useState(false);
  const [addContentDialogOpen, setAddContentDialogOpen] = useState(false);
  const [editContentDialogOpen, setEditContentDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<EducationContent | null>(null);
  const [createTemplateDialogOpen, setCreateTemplateDialogOpen] = useState(false);
  const [editTemplateDialogOpen, setEditTemplateDialogOpen] = useState(false);
  const [useTemplateDialogOpen, setUseTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<NudgeTemplate | null>(null);
  const [targetedCampaignDialogOpen, setTargetedCampaignDialogOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<HostSegment | null>(null);
  const [editCampaignDialogOpen, setEditCampaignDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<NudgeCampaign | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel' | 'pdf'>('csv');
  const [exportDataType, setExportDataType] = useState<'campaigns' | 'content' | 'segments' | 'metrics'>('campaigns');
  const [exportDateRange, setExportDateRange] = useState('30d');
  const [isExporting, setIsExporting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [globalAutoEnabled, setGlobalAutoEnabled] = useState(true);
  const [createNudgeDialogOpen, setCreateNudgeDialogOpen] = useState(false);
  const [editTipDialogOpen, setEditTipDialogOpen] = useState(false);
  const [tipAnalyticsDialogOpen, setTipAnalyticsDialogOpen] = useState(false);
  const [vehicleMaintenanceDialogOpen, setVehicleMaintenanceDialogOpen] = useState(false);
  const [disputeResolutionDialogOpen, setDisputeResolutionDialogOpen] = useState(false);
  const [viewDocDialogOpen, setViewDocDialogOpen] = useState(false);
  const [editDocDialogOpen, setEditDocDialogOpen] = useState(false);
  const [addDocDialogOpen, setAddDocDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<DocumentationItem | null>(null);
  const [selectedTip, setSelectedTip] = useState<TriggeredTip | null>(null);
  const [customNudgeData, setCustomNudgeData] = useState({
    title: '',
    description: '',
    triggerCondition: '',
    messageContent: '',
    cta: '',
    severity: 'info' as 'info' | 'warning' | 'error' | 'success',
    impact: 'Medium Impact',
    autoEnabled: true,
  });
  const [campaignData, setCampaignData] = useState({
    name: '',
    type: 'engagement' as 'onboarding' | 'performance' | 'engagement' | 'compliance' | 'feature',
    objective: 'engagement',
    message: '',
    cta: '',
    targetAudience: 'all-hosts',
    sendMethod: 'email' as 'email' | 'sms' | 'push' | 'in-app',
    sendTime: 'immediate',
    scheduledDate: '',
  });
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    category: 'Onboarding',
    trigger: '',
    title: '',
    message: '',
    cta: '',
    targetAudience: 'all-hosts',
    sendMethod: 'email' as 'email' | 'sms' | 'push' | 'in-app',
  });
  const [newContent, setNewContent] = useState({
    title: '',
    type: 'video' as 'video' | 'article' | 'quiz' | 'checklist' | 'webinar',
    category: 'onboarding' as 'onboarding' | 'optimization' | 'safety' | 'compliance' | 'growth',
    duration: '',
    url: '',
    description: ''
  });
  const [createModuleDialogOpen, setCreateModuleDialogOpen] = useState(false);
  const [newModule, setNewModule] = useState({
    title: '',
    description: '',
    duration: '',
    category: 'Onboarding',
    difficulty: 'Beginner' as 'Beginner' | 'Intermediate' | 'Advanced',
    completionRate: 0
  });
  const [trainingModulesList, setTrainingModulesList] = useState<TrainingModule[]>([]);

  const nudgeMetrics: NudgeMetric[] = [
    {
      id: 'engagement',
      title: 'Engagement Rate',
      value: 68.5,
      change: 8.4,
      changeDirection: 'up',
      icon: <Psychology />,
      color: '#2196F3',
      description: 'Host interaction with educational content'
    },
    {
      id: 'completion',
      title: 'Course Completion',
      value: 74.2,
      change: 5.7,
      changeDirection: 'up',
      icon: <School />,
      color: '#4CAF50',
      description: 'Educational module completion rate'
    },
    {
      id: 'performance',
      title: 'Performance Lift',
      value: 23.8,
      change: 3.2,
      changeDirection: 'up',
      icon: <TrendingUp />,
      color: '#FF9800',
      description: 'Performance improvement post-education'
    },
    {
      id: 'satisfaction',
      title: 'Host Satisfaction',
      value: 4.6,
      change: 0.3,
      changeDirection: 'up',
      icon: <Star />,
      color: '#9C27B0',
      description: 'Average education content rating (1-5)'
    },
  ];

  const [activeCampaigns, setActiveCampaigns] = useState<NudgeCampaign[]>([
    {
      id: '1',
      name: 'New Host Onboarding Journey',
      type: 'onboarding',
      status: 'active',
      targetAudience: 'New Hosts (0-30 days)',
      sendMethod: 'email',
      startDate: '2024-01-01',
      delivered: 1247,
      opened: 856,
      clicked: 423,
      converted: 167,
      segments: ['new-hosts', 'owners'],
      abTestVariant: 'A'
    },
    {
      id: '2',
      name: 'Vehicle Optimization Tips',
      type: 'performance',
      status: 'active',
      targetAudience: 'Low-performing Hosts',
      sendMethod: 'push',
      startDate: '2024-01-10',
      delivered: 892,
      opened: 634,
      clicked: 298,
      converted: 156,
      segments: ['low-performers', 'fleet-managers'],
      abTestVariant: 'B'
    },
    {
      id: '3',
      name: 'Safety Best Practices',
      type: 'compliance',
      status: 'active',
      targetAudience: 'All Active Hosts',
      sendMethod: 'in-app',
      startDate: '2024-01-15',
      delivered: 3421,
      opened: 2567,
      clicked: 1234,
      converted: 892,
      segments: ['all-hosts'],
    },
    {
      id: '4',
      name: 'Revenue Growth Strategies',
      type: 'feature',
      status: 'paused',
      targetAudience: 'High-performing Hosts',
      sendMethod: 'email',
      startDate: '2024-01-05',
      endDate: '2024-01-20',
      delivered: 567,
      opened: 445,
      clicked: 234,
      converted: 123,
      segments: ['high-performers', 'brokers'],
    },
  ]);

  const hostSegments: HostSegment[] = [
    {
      id: 'new-hosts',
      name: 'New Hosts',
      criteria: ['Registered < 30 days', 'Bookings < 5'],
      hostCount: 342,
      avgPerformance: 67.2,
      color: '#2196F3',
      description: 'Recently joined hosts needing onboarding'
    },
    {
      id: 'low-performers',
      name: 'Low Performers',
      criteria: ['Rating < 4.0', 'Cancellation rate > 15%'],
      hostCount: 189,
      avgPerformance: 45.8,
      color: '#F44336',
      description: 'Hosts requiring performance improvement'
    },
    {
      id: 'high-performers',
      name: 'High Performers',
      criteria: ['Rating > 4.7', 'Booking acceptance > 90%'],
      hostCount: 567,
      avgPerformance: 92.4,
      color: '#4CAF50',
      description: 'Top-performing hosts for retention'
    },
    {
      id: 'inactive-hosts',
      name: 'Inactive Hosts',
      criteria: ['No bookings in 30 days', 'Last login > 14 days'],
      hostCount: 234,
      avgPerformance: 32.1,
      color: '#9E9E9E',
      description: 'Disengaged hosts needing re-activation'
    },
  ];

  const educationContent: EducationContent[] = [
    {
      id: '1',
      title: 'Host Onboarding Masterclass',
      type: 'video',
      category: 'onboarding',
      duration: '25 min',
      completionRate: 78.4,
      rating: 4.7,
      views: 1247,
      createdAt: '2024-01-01',
      status: 'published'
    },
    {
      id: '2',
      title: 'Vehicle Photography Best Practices',
      type: 'article',
      category: 'optimization',
      duration: '8 min read',
      completionRate: 65.2,
      rating: 4.5,
      views: 892,
      createdAt: '2024-01-05',
      status: 'published'
    },
    {
      id: '3',
      title: 'Safety Protocol Quiz',
      type: 'quiz',
      category: 'safety',
      duration: '5 min',
      completionRate: 89.7,
      rating: 4.3,
      views: 2134,
      createdAt: '2024-01-10',
      status: 'published'
    },
    {
      id: '4',
      title: 'Revenue Optimization Strategies',
      type: 'webinar',
      category: 'growth',
      duration: '45 min',
      completionRate: 56.8,
      rating: 4.8,
      views: 456,
      createdAt: '2024-01-12',
      status: 'published'
    },
  ];

  const nudgeTemplates: NudgeTemplate[] = [
    {
      id: '1',
      name: 'Welcome New Host',
      category: 'Onboarding',
      trigger: 'Account created',
      title: 'Welcome to PolarRide! Let\'s get you started',
      message: 'Complete your profile and list your first vehicle to start earning.',
      cta: 'Complete Setup',
      effectiveness: 72.4,
      usageCount: 1247,
      lastUsed: '2024-01-15'
    },
    {
      id: '2',
      name: 'Low Rating Alert',
      category: 'Performance',
      trigger: 'Rating drops below 4.0',
      title: 'Improve your host rating with these tips',
      message: 'Your rating has dropped. Here are proven strategies to improve guest satisfaction.',
      cta: 'View Tips',
      effectiveness: 68.1,
      usageCount: 89,
      lastUsed: '2024-01-14'
    },
    {
      id: '3',
      name: 'Booking Optimization',
      category: 'Growth',
      trigger: 'Low booking rate',
      title: 'Boost your bookings with these strategies',
      message: 'Increase your booking rate by 40% with these proven optimization techniques.',
      cta: 'Learn More',
      effectiveness: 85.3,
      usageCount: 234,
      lastUsed: '2024-01-13'
    },
  ];

  const nudgeHistory: HostNudgeHistory[] = [
    {
      id: '1',
      hostId: 'host_001',
      hostName: 'John Doe',
      hostType: 'Owner',
      nudgeType: 'Performance',
      title: 'Improve your host rating',
      sentAt: '2024-01-15T10:30:00Z',
      opened: true,
      clicked: true,
      converted: true,
      score: 8.5
    },
    {
      id: '2',
      hostId: 'host_002',
      hostName: 'Jane Smith',
      hostType: 'Fleet Manager',
      nudgeType: 'Onboarding',
      title: 'Complete your profile setup',
      sentAt: '2024-01-15T09:15:00Z',
      opened: true,
      clicked: false,
      converted: false,
      score: 4.2
    },
    {
      id: '3',
      hostId: 'host_003',
      hostName: 'Mike Johnson',
      hostType: 'Broker',
      nudgeType: 'Feature',
      title: 'Try our new pricing tool',
      sentAt: '2024-01-15T08:45:00Z',
      opened: false,
      clicked: false,
      converted: false,
      score: 0
    },
  ];

  const [triggeredTips, setTriggeredTips] = useState<TriggeredTip[]>([
    {
      id: '1',
      title: 'Substitution Prevention',
      description: 'Triggered when host has 5+ substitutions in 30 days',
      icon: <CarRepair />,
      severity: 'warning',
      triggerCondition: 'Host has 5+ substitutions in 30 days',
      autoEnabled: true,
      impact: 'High Impact',
      currentTriggers: 8,
      lastTriggered: '6 hours',
      messageContent: 'We noticed some recent car substitutions. This can impact your reputation and earnings. Our guide can help you avoid substitutions and improve guest satisfaction.',
      cta: 'Vehicle Maintenance Tips'
    },
    {
      id: '2',
      title: 'Dispute Resolution',
      description: 'Triggered when host has 2+ disputes in 14 days',
      icon: <Warning />,
      severity: 'warning',
      triggerCondition: 'Host has 2+ disputes in 14 days',
      autoEnabled: true,
      impact: 'Medium Impact',
      currentTriggers: 5,
      lastTriggered: '2 hours',
      messageContent: 'We ve noticed some recent disputes with your bookings. Disputes can impact your reputation and can lead to reduced visibility. Learn how to manage your calendar effectively.',
      cta: 'Dispute Resolution Guide'
    },
    {
      id: '3',
      title: 'Cancellation Reduction',
      description: 'Triggered when host has 5+ cancellations in 30 days',
      icon: <Cancel />,
      severity: 'warning',
      triggerCondition: 'Host has 5+ cancellations in 30 days',
      autoEnabled: true,
      impact: 'High Impact',
      currentTriggers: 12,
      lastTriggered: '1 day',
      messageContent: 'We ve noticed some recent booking cancellations. Cancellations significantly impact your reputation and can lead to reduced visibility. Learn how to manage your calendar effectively.',
      cta: 'Calendar Management Tips'
    }
  ]);

  const documentationItems: DocumentationItem[] = [
    {
      id: '1',
      title: 'Host Onboarding Guide',
      description: 'Complete walkthrough for new hosts',
      category: 'guide',
      icon: <MenuBook />,
      url: '/docs/host-onboarding',
      isExternal: false,
      lastUpdated: '2024-01-10'
    },
    {
      id: '2',
      title: 'Listing Optimization',
      description: 'Best practices for creating optimal listings',
      category: 'guide',
      icon: <TrendingUp />,
      url: '/docs/listing-optimization',
      isExternal: false,
      lastUpdated: '2024-01-08'
    },
    {
      id: '3',
      title: 'Verification Requirements',
      description: 'Documents and verification guidelines',
      category: 'requirement',
      icon: <VerificationIcon />,
      url: '/docs/verification',
      isExternal: false,
      lastUpdated: '2024-01-05'
    },
    {
      id: '4',
      title: 'SLA Compliance Guide',
      description: 'Understanding service level agreements',
      category: 'policy',
      icon: <Policy />,
      url: '/docs/sla-compliance',
      isExternal: false,
      lastUpdated: '2024-01-03'
    }
  ];

  const videoGuides: VideoGuide[] = [
    {
      id: '1',
      title: 'Advanced Host Strategies',
      description: 'Master tips to drive earnings',
      duration: '12 min 6 sec',
      thumbnail: '/api/placeholder/300/200',
      category: 'Advanced Strategies',
      views: 2564,
      rating: 4.8
    },
    {
      id: '2',
      title: 'Handling Guest Communications',
      description: 'Effective communication strategies',
      duration: '8 min 42 sec',
      thumbnail: '/api/placeholder/300/200',
      category: 'Communication',
      views: 1892,
      rating: 4.6
    }
  ];

  const trainingModules: TrainingModule[] = [
    {
      id: '1',
      title: 'Host Excellence Program',
      description: '3 modules • 30 min completion time',
      duration: '30 min',
      completionRate: 78,
      category: 'Excellence',
      difficulty: 'Intermediate'
    },
    {
      id: '2',
      title: 'Dispute Resolution Mastery',
      description: '2 modules • 20 min completion time',
      duration: '20 min',
      completionRate: 56,
      category: 'Conflict Resolution',
      difficulty: 'Advanced'
    },
    {
      id: '3',
      title: 'Vehicle Maintenance 101',
      description: '4 modules • 45 min completion time',
      duration: '45 min',
      completionRate: 89,
      category: 'Maintenance',
      difficulty: 'Beginner'
    },
    {
      id: '4',
      title: 'Verification & Trust Building',
      description: '3 modules • 25 min completion time',
      duration: '25 min',
      completionRate: 100,
      category: 'Trust & Safety',
      difficulty: 'Beginner'
    }
  ];

  const nudgeEffectivenessMetrics: NudgeEffectivenessMetric[] = [
    {
      id: 'nudgesSent',
      title: 'Nudges Sent',
      value: 1247,
      change: 8.2,
      changeDirection: 'up',
      description: 'Total nudges delivered this period'
    },
    {
      id: 'openRate',
      title: 'Open Rate',
      value: 76,
      change: 4.3,
      changeDirection: 'up',
      description: 'Percentage of nudges opened'
    },
    {
      id: 'responseClickRate',
      title: 'Response Click Rate',
      value: 42,
      change: 2.1,
      changeDirection: 'up',
      description: 'Click-through rate on nudge CTAs'
    },
    {
      id: 'nudgeConversionRate',
      title: 'Nudge Conversion Rate',
      value: 63,
      change: 5.4,
      changeDirection: 'up',
      description: 'Conversion rate from nudge to action'
    }
  ];

  const engagementTrendData = [
    { name: 'Week 1', engagement: 65, completion: 70, performance: 20 },
    { name: 'Week 2', engagement: 68, completion: 72, performance: 22 },
    { name: 'Week 3', engagement: 70, completion: 74, performance: 24 },
    { name: 'Week 4', engagement: 68, completion: 74, performance: 23 },
  ];

  const segmentPerformanceData = [
    { name: 'New Hosts', value: 67.2, fill: '#2196F3' },
    { name: 'Low Performers', value: 45.8, fill: '#F44336' },
    { name: 'High Performers', value: 92.4, fill: '#4CAF50' },
    { name: 'Inactive', value: 32.1, fill: '#9E9E9E' },
  ];

  const contentTypeData = [
    { name: 'Videos', count: 24, engagement: 78 },
    { name: 'Articles', count: 45, engagement: 65 },
    { name: 'Quizzes', count: 12, engagement: 89 },
    { name: 'Webinars', count: 8, engagement: 57 },
  ];

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active': return '#4CAF50';
      case 'paused': return '#FF9800';
      case 'draft': return '#9E9E9E';
      case 'completed': return '#2196F3';
      default: return '#9E9E9E';
    }
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'onboarding': return '#2196F3';
      case 'performance': return '#FF9800';
      case 'engagement': return '#9C27B0';
      case 'compliance': return '#F44336';
      case 'feature': return '#4CAF50';
      default: return '#9E9E9E';
    }
  };

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <VideoLibrary />;
      case 'article': return <Article />;
      case 'quiz': return <Quiz />;
      case 'webinar': return <PlayCircle />;
      case 'checklist': return <Assignment />;
      default: return <Article />;
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateConversionRate = (campaign: NudgeCampaign): number => {
    return campaign.delivered > 0 ? (campaign.converted / campaign.delivered) * 100 : 0;
  };

  const calculateClickRate = (campaign: NudgeCampaign): number => {
    return campaign.opened > 0 ? (campaign.clicked / campaign.opened) * 100 : 0;
  };

  const handleToggleCampaignStatus = (campaignId: string) => {
    setActiveCampaigns(prevCampaigns =>
      prevCampaigns.map(campaign =>
        campaign.id === campaignId
          ? {
              ...campaign,
              status: campaign.status === 'active' ? 'paused' : 'active'
            }
          : campaign
      )
    );
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, campaign: NudgeCampaign) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedCampaign(campaign);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleViewDetails = () => {
    if (selectedCampaign) {
      setEditCampaignDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDuplicateCampaign = () => {
    if (selectedCampaign) {
      const duplicatedCampaign: NudgeCampaign = {
        ...selectedCampaign,
        id: `${Date.now()}`,
        name: `${selectedCampaign.name} (Copy)`,
        status: 'draft',
      };
      setActiveCampaigns(prev => [...prev, duplicatedCampaign]);
    }
    handleMenuClose();
  };

  const handleExportReport = () => {
    if (selectedCampaign) {
      // Create CSV export
      const csvContent = `Campaign Report\n\nCampaign Name,${selectedCampaign.name}\nType,${selectedCampaign.type}\nStatus,${selectedCampaign.status}\nTarget Audience,${selectedCampaign.targetAudience}\nSend Method,${selectedCampaign.sendMethod}\nStart Date,${selectedCampaign.startDate}\n\nMetrics\nDelivered,${selectedCampaign.delivered}\nOpened,${selectedCampaign.opened}\nClicked,${selectedCampaign.clicked}\nConverted,${selectedCampaign.converted}\nOpen Rate,${((selectedCampaign.opened / selectedCampaign.delivered) * 100).toFixed(2)}%\nClick Rate,${calculateClickRate(selectedCampaign).toFixed(2)}%\nConversion Rate,${calculateConversionRate(selectedCampaign).toFixed(2)}%`;

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `campaign-report-${selectedCampaign.id}-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
    handleMenuClose();
  };

  const handleArchiveCampaign = () => {
    if (selectedCampaign) {
      setActiveCampaigns(prevCampaigns =>
        prevCampaigns.map(campaign =>
          campaign.id === selectedCampaign.id
            ? { ...campaign, status: 'completed' }
            : campaign
        )
      );
    }
    handleMenuClose();
  };

  const handleDeleteCampaign = () => {
    if (selectedCampaign) {
      setActiveCampaigns(prevCampaigns =>
        prevCampaigns.filter(campaign => campaign.id !== selectedCampaign.id)
      );
    }
    handleMenuClose();
  };

  const handleExportData = () => {
    setIsExporting(true);

    setTimeout(() => {
      let csvContent = '';
      let filename = '';

      if (exportDataType === 'campaigns') {
        csvContent = 'Campaign Name,Type,Status,Target Audience,Send Method,Start Date,Delivered,Opened,Clicked,Converted,Open Rate,Click Rate,Conversion Rate\n';
        activeCampaigns.forEach(campaign => {
          const openRate = ((campaign.opened / campaign.delivered) * 100).toFixed(2);
          const clickRate = calculateClickRate(campaign).toFixed(2);
          const conversionRate = calculateConversionRate(campaign).toFixed(2);
          csvContent += `"${campaign.name}",${campaign.type},${campaign.status},"${campaign.targetAudience}",${campaign.sendMethod},${campaign.startDate},${campaign.delivered},${campaign.opened},${campaign.clicked},${campaign.converted},${openRate}%,${clickRate}%,${conversionRate}%\n`;
        });
        filename = `education-campaigns-${new Date().toISOString().split('T')[0]}.csv`;
      } else if (exportDataType === 'content') {
        csvContent = 'Title,Type,Category,Duration,Completion Rate,Rating,Views,Status,Created Date\n';
        educationContent.forEach(content => {
          csvContent += `"${content.title}",${content.type},${content.category},"${content.duration}",${content.completionRate}%,${content.rating},${content.views},${content.status},${content.createdAt}\n`;
        });
        filename = `education-content-${new Date().toISOString().split('T')[0]}.csv`;
      } else if (exportDataType === 'segments') {
        csvContent = 'Segment Name,Host Count,Average Performance,Criteria,Description\n';
        hostSegments.forEach(segment => {
          csvContent += `"${segment.name}",${segment.hostCount},${segment.avgPerformance}%,"${segment.criteria.join('; ')}","${segment.description}"\n`;
        });
        filename = `host-segments-${new Date().toISOString().split('T')[0]}.csv`;
      } else if (exportDataType === 'metrics') {
        csvContent = 'Metric,Value,Change,Change Direction,Description\n';
        nudgeMetrics.forEach(metric => {
          csvContent += `"${metric.title}",${metric.value}%,${metric.change}%,${metric.changeDirection},"${metric.description}"\n`;
        });
        filename = `education-metrics-${new Date().toISOString().split('T')[0]}.csv`;
      }

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);

      setIsExporting(false);
    }, 1000);
  };

  const handleExportActiveCampaigns = () => {
    // Export the currently filtered campaigns
    let csvContent = 'Campaign Name,Type,Status,Target Audience,Send Method,Start Date,Delivered,Opened,Clicked,Converted,Open Rate,Click Rate,Conversion Rate\n';

    filteredCampaigns.forEach(campaign => {
      const openRate = ((campaign.opened / campaign.delivered) * 100).toFixed(2);
      const clickRate = calculateClickRate(campaign).toFixed(2);
      const conversionRate = calculateConversionRate(campaign).toFixed(2);
      csvContent += `"${campaign.name}",${campaign.type},${campaign.status},"${campaign.targetAudience}",${campaign.sendMethod},${campaign.startDate},${campaign.delivered},${campaign.opened},${campaign.clicked},${campaign.converted},${openRate}%,${clickRate}%,${conversionRate}%\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `active-campaigns-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreateCampaign = () => {
    // Validate required fields
    if (!campaignData.name || !campaignData.message) {
      alert('Please fill in all required fields (Campaign Name and Message)');
      return;
    }

    // Create new campaign
    const newCampaign: NudgeCampaign = {
      id: `campaign-${Date.now()}`,
      name: campaignData.name,
      type: campaignData.type,
      status: campaignData.sendTime === 'immediate' ? 'active' : 'draft',
      targetAudience: campaignData.targetAudience,
      sendMethod: campaignData.sendMethod,
      startDate: campaignData.sendTime === 'immediate'
        ? new Date().toISOString().split('T')[0]
        : campaignData.scheduledDate,
      delivered: 0,
      opened: 0,
      clicked: 0,
      converted: 0,
      segments: campaignData.targetAudience === 'all-hosts' ? ['all-hosts'] : [campaignData.targetAudience],
    };

    // Add to campaigns list
    setActiveCampaigns(prev => [...prev, newCampaign]);

    // Reset form and close dialog
    setCampaignData({
      name: '',
      type: 'engagement',
      objective: 'engagement',
      message: '',
      cta: '',
      targetAudience: 'all-hosts',
      sendMethod: 'email',
      sendTime: 'immediate',
      scheduledDate: '',
    });
    setCampaignDialogOpen(false);

    // Show success message
    alert(`Campaign "${newCampaign.name}" has been created successfully!\n\n${
      campaignData.sendTime === 'immediate'
        ? 'The campaign is now active and sending awareness to hosts via ' + campaignData.sendMethod + '.'
        : 'The campaign is scheduled for ' + campaignData.scheduledDate + '.'
    }`);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);

    // Simulate API refresh with timeout
    setTimeout(() => {
      // In a real application, this would fetch fresh data from the API
      // For now, we'll just simulate the refresh

      setIsRefreshing(false);

      // Show success notification
      alert('Data refreshed successfully!\n\nAll metrics, campaigns, and content have been updated with the latest information.');
    }, 1500);
  };

  const handleGlobalAutoEnabledToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isEnabled = event.target.checked;
    setGlobalAutoEnabled(isEnabled);

    // Update all triggered tips to match global setting
    setTriggeredTips(prevTips =>
      prevTips.map(tip => ({
        ...tip,
        autoEnabled: isEnabled
      }))
    );
  };

  const handleTipAutoEnabledToggle = (tipId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const isEnabled = event.target.checked;

    setTriggeredTips(prevTips =>
      prevTips.map(tip =>
        tip.id === tipId
          ? { ...tip, autoEnabled: isEnabled }
          : tip
      )
    );

    // Update global switch if all tips are enabled/disabled
    const updatedTips = triggeredTips.map(tip =>
      tip.id === tipId ? { ...tip, autoEnabled: isEnabled } : tip
    );
    const allEnabled = updatedTips.every(tip => tip.autoEnabled);
    const allDisabled = updatedTips.every(tip => !tip.autoEnabled);

    if (allEnabled) {
      setGlobalAutoEnabled(true);
    } else if (allDisabled) {
      setGlobalAutoEnabled(false);
    }
  };

  const handleCreateCustomNudge = () => {
    // Validate required fields
    if (!customNudgeData.title || !customNudgeData.triggerCondition || !customNudgeData.messageContent) {
      alert('Please fill in all required fields (Title, Trigger Condition, and Message)');
      return;
    }

    // Create new triggered tip
    const newTip: TriggeredTip = {
      id: `custom-${Date.now()}`,
      title: customNudgeData.title,
      description: customNudgeData.description,
      icon: <Lightbulb />,
      severity: customNudgeData.severity,
      triggerCondition: customNudgeData.triggerCondition,
      autoEnabled: customNudgeData.autoEnabled,
      impact: customNudgeData.impact,
      currentTriggers: 0,
      lastTriggered: 'Never',
      messageContent: customNudgeData.messageContent,
      cta: customNudgeData.cta || 'Learn More'
    };

    // Add to triggered tips list
    setTriggeredTips(prev => [...prev, newTip]);

    // Reset form and close dialog
    setCustomNudgeData({
      title: '',
      description: '',
      triggerCondition: '',
      messageContent: '',
      cta: '',
      severity: 'info',
      impact: 'Medium Impact',
      autoEnabled: true,
    });
    setCreateNudgeDialogOpen(false);

    // Show success message
    alert(`Custom nudge "${newTip.title}" has been created successfully!\n\nThe nudge is now ${newTip.autoEnabled ? 'active and will trigger automatically' : 'inactive'} based on your trigger condition.`);
  };

  const handleEditTip = (tip: TriggeredTip) => {
    setSelectedTip(tip);
    setEditTipDialogOpen(true);
  };

  const handleSaveTipEdit = () => {
    if (!selectedTip) return;

    setTriggeredTips(prevTips =>
      prevTips.map(tip =>
        tip.id === selectedTip.id ? selectedTip : tip
      )
    );

    setEditTipDialogOpen(false);
    alert(`Triggered tip "${selectedTip.title}" has been updated successfully!`);
  };

  const handleViewTipAnalytics = (tip: TriggeredTip) => {
    setSelectedTip(tip);
    setTipAnalyticsDialogOpen(true);
  };

  const handleOpenVehicleMaintenance = () => {
    setVehicleMaintenanceDialogOpen(true);
  };

  const handleOpenDisputeResolution = () => {
    setDisputeResolutionDialogOpen(true);
  };

  const filteredCampaigns = activeCampaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    const matchesType = typeFilter === 'all' || campaign.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Host Education Nudges
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Behavioral nudging and educational content delivery system
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="90d">Last 90 days</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCampaignDialogOpen(true)}>
            New Campaign
          </Button>
        </Box>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {nudgeMetrics.map((metric) => (
          <Grid item xs={12} sm={6} md={3} key={metric.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: 1, 
                    backgroundColor: metric.color + '20',
                    color: metric.color,
                    mr: 2
                  }}>
                    {metric.icon}
                  </Box>
                  <Typography variant="h6" component="div">
                    {metric.title}
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" gutterBottom>
                  {metric.id === 'satisfaction' ? metric.value : `${metric.value}%`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {metric.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {metric.changeDirection === 'up' ? (
                      <TrendingUp sx={{ color: '#4CAF50', fontSize: 16 }} />
                    ) : (
                      <TrendingDown sx={{ color: '#F44336', fontSize: 16 }} />
                    )}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: metric.changeDirection === 'up' ? '#4CAF50' : '#F44336',
                        ml: 0.5
                      }}
                    >
                      {metric.change > 0 ? '+' : ''}{metric.change}%
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs Navigation */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<Lightbulb />} label="Triggered Tips" />
          <Tab icon={<Campaign />} label="Active Campaigns" />
          <Tab icon={<Group />} label="Host Segments" />
          <Tab icon={<School />} label="Content Library" />
          <Tab icon={<Psychology />} label="Nudge Templates" />
          <Tab icon={<MenuBook />} label="Documentation" />
          <Tab icon={<VideoLibrary />} label="Video Guides" />
          <Tab icon={<Assignment />} label="Training Modules" />
          <Tab icon={<Analytics />} label="Performance Analytics" />
          <Tab icon={<AutoGraph />} label="Effectiveness Metrics" />
          <Tab icon={<Timeline />} label="Nudge History" />
          <Tab icon={<Download />} label="Export" />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Triggered Tips
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Automatically triggered suggestions based on host performance metrics
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={globalAutoEnabled}
                      onChange={handleGlobalAutoEnabledToggle}
                    />
                  }
                  label="Auto-enabled"
                />
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setCreateNudgeDialogOpen(true)}
                >
                  Create Custom Nudge
                </Button>
              </Box>
            </Box>

            <Grid container spacing={3}>
              {triggeredTips.map((tip) => (
                <Grid item xs={12} key={tip.id}>
                  <Card variant="outlined" sx={{ 
                    borderLeft: `4px solid ${tip.severity === 'warning' ? '#FF9800' : tip.severity === 'info' ? '#2196F3' : '#4CAF50'}`,
                    backgroundColor: tip.severity === 'warning' ? '#FFF8E1' : tip.severity === 'info' ? '#E3F2FD' : '#E8F5E9'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ 
                            p: 1, 
                            borderRadius: 1, 
                            backgroundColor: tip.severity === 'warning' ? '#FF9800' : tip.severity === 'info' ? '#2196F3' : '#4CAF50',
                            color: 'white'
                          }}>
                            {tip.icon}
                          </Box>
                          <Box>
                            <Typography variant="h6" component="div">
                              {tip.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {tip.description}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Chip 
                            label={tip.impact} 
                            size="small" 
                            color={tip.impact.includes('High') ? 'error' : tip.impact.includes('Medium') ? 'warning' : 'success'}
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={tip.autoEnabled}
                                onChange={handleTipAutoEnabledToggle(tip.id)}
                              />
                            }
                            label="Auto-enabled"
                          />
                        </Box>
                      </Box>

                      <Alert severity={tip.severity} sx={{ mb: 2 }}>
                        <AlertTitle>Message Content</AlertTitle>
                        {tip.messageContent}
                      </Alert>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Current Triggers
                          </Typography>
                          <Typography variant="h6">
                            {tip.currentTriggers}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Last Triggered
                          </Typography>
                          <Typography variant="body2">
                            {tip.lastTriggered} ago
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            if (tip.cta === 'Vehicle Maintenance Tips') {
                              handleOpenVehicleMaintenance();
                            } else if (tip.cta === 'Dispute Resolution Guide') {
                              handleOpenDisputeResolution();
                            }
                          }}
                        >
                          {tip.cta}
                        </Button>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Trigger Condition:</strong> {tip.triggerCondition}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Edit />}
                            onClick={() => handleEditTip(tip)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Analytics />}
                            onClick={() => handleViewTipAnalytics(tip)}
                          >
                            View Analytics
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {selectedTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Active Campaigns ({filteredCampaigns.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" startIcon={<Science />} onClick={() => setAbTestDialogOpen(true)}>
                  A/B Test
                </Button>
                <Button variant="outlined" startIcon={<Download />} onClick={handleExportActiveCampaigns}>
                  Export
                </Button>
              </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="onboarding">Onboarding</MenuItem>
                  <MenuItem value="performance">Performance</MenuItem>
                  <MenuItem value="engagement">Engagement</MenuItem>
                  <MenuItem value="compliance">Compliance</MenuItem>
                  <MenuItem value="feature">Feature</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Campaigns Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Target Audience</TableCell>
                    <TableCell>Delivery</TableCell>
                    <TableCell>Engagement</TableCell>
                    <TableCell>Conversion</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCampaigns.map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {campaign.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Started {formatDate(campaign.startDate)}
                          </Typography>
                          {campaign.abTestVariant && (
                            <Chip 
                              label={`Variant ${campaign.abTestVariant}`} 
                              size="small" 
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={campaign.type}
                          size="small"
                          sx={{
                            backgroundColor: getTypeColor(campaign.type) + '20',
                            color: getTypeColor(campaign.type),
                            textTransform: 'capitalize'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={campaign.status}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(campaign.status) + '20',
                            color: getStatusColor(campaign.status),
                            textTransform: 'capitalize'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {campaign.targetAudience}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          via {campaign.sendMethod}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {campaign.delivered.toLocaleString()} sent
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(campaign.opened / campaign.delivered) * 100}
                            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {((campaign.opened / campaign.delivered) * 100).toFixed(1)}% opened
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {calculateClickRate(campaign).toFixed(1)}% CTR
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {campaign.clicked.toLocaleString()} clicks
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {calculateConversionRate(campaign).toFixed(1)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {campaign.converted.toLocaleString()} conversions
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedCampaign(campaign);
                              setEditCampaignDialogOpen(true);
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleCampaignStatus(campaign.id)}
                            title={campaign.status === 'active' ? 'Pause campaign' : 'Resume campaign'}
                          >
                            {campaign.status === 'active' ? <Pause /> : <PlayCircle />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, campaign)}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {selectedTab === 2 && (
        <Grid container spacing={3}>
          {hostSegments.map((segment) => (
            <Grid item xs={12} md={6} lg={3} key={segment.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      backgroundColor: segment.color,
                      mr: 1
                    }} />
                    <Typography variant="h6" component="div">
                      {segment.name}
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" gutterBottom>
                    {segment.hostCount.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {segment.description}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Avg Performance: {segment.avgPerformance}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={segment.avgPerformance}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="body2">Criteria</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {segment.criteria.map((criterion, index) => (
                        <Chip
                          key={index}
                          label={criterion}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      ))}
                    </AccordionDetails>
                  </Accordion>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={<Campaign />}
                      onClick={() => {
                        setSelectedSegment(segment);
                        setTargetedCampaignDialogOpen(true);
                      }}
                    >
                      Create Targeted Campaign
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {selectedTab === 3 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Educational Content Library ({educationContent.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAddContentDialogOpen(true)}
              >
                Add Content
              </Button>
            </Box>

            <Grid container spacing={3}>
              {educationContent.map((content) => (
                <Grid item xs={12} md={6} lg={4} key={content.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ mr: 2, color: 'primary.main' }}>
                          {getContentTypeIcon(content.type)}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="div" noWrap>
                            {content.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {content.category} • {content.duration}
                          </Typography>
                        </Box>
                        <Chip
                          label={content.status}
                          size="small"
                          color={content.status === 'published' ? 'success' : 'default'}
                        />
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2">Completion Rate</Typography>
                          <Typography variant="body2">{content.completionRate}%</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={content.completionRate}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Star sx={{ color: '#3b82f6', fontSize: 16, mr: 0.5 }} />
                          <Typography variant="body2">{content.rating}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {content.views.toLocaleString()} views
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          fullWidth
                          startIcon={<Edit />}
                          onClick={() => {
                            setSelectedContent(content);
                            setEditContentDialogOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          fullWidth
                          startIcon={<Analytics />}
                          onClick={() => {
                            setSelectedContent(content);
                            setAnalyticsDialogOpen(true);
                          }}
                        >
                          Analytics
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {selectedTab === 4 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Nudge Templates ({nudgeTemplates.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCreateTemplateDialogOpen(true)}
              >
                Create Template
              </Button>
            </Box>

            <Grid container spacing={3}>
              {nudgeTemplates.map((template) => (
                <Grid item xs={12} md={6} key={template.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" component="div">
                            {template.name}
                          </Typography>
                          <Chip 
                            label={template.category} 
                            size="small" 
                            sx={{ mt: 1 }}
                          />
                        </Box>
                        <Typography variant="h6" color="primary.main">
                          {template.effectiveness}%
                        </Typography>
                      </Box>

                      <Alert severity="info" sx={{ mb: 2 }}>
                        <AlertTitle>Trigger: {template.trigger}</AlertTitle>
                      </Alert>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          {template.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {template.message}
                        </Typography>
                        <Button size="small" variant="outlined">
                          {template.cta}
                        </Button>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Used {template.usageCount} times
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last used {formatDate(template.lastUsed)}
                        </Typography>
                      </Box>

                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          fullWidth
                          startIcon={<Edit />}
                          onClick={() => {
                            setSelectedTemplate(template);
                            setEditTemplateDialogOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          fullWidth
                          startIcon={<PlayCircle />}
                          onClick={() => {
                            setSelectedTemplate(template);
                            setUseTemplateDialogOpen(true);
                          }}
                        >
                          Use Template
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {selectedTab === 5 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Documentation ({documentationItems.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAddDocDialogOpen(true)}
              >
                Add Documentation
              </Button>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Comprehensive guides and documentation for hosts
            </Typography>

            <Grid container spacing={3}>
              {documentationItems.map((item) => (
                <Grid item xs={12} md={6} lg={4} key={item.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ mr: 2, color: 'primary.main' }}>
                          {item.icon}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="div">
                            {item.title}
                          </Typography>
                          <Chip 
                            label={item.category} 
                            size="small" 
                            sx={{ mt: 1, textTransform: 'capitalize' }}
                          />
                        </Box>
                        {item.isExternal && <OpenInNew />}
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {item.description}
                      </Typography>

                      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                        Last updated: {new Date(item.lastUpdated).toLocaleDateString()}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          fullWidth
                          startIcon={<OpenInNew />}
                          onClick={() => {
                            setSelectedDoc(item);
                            setViewDocDialogOpen(true);
                          }}
                        >
                          View Guide
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          fullWidth
                          startIcon={<Edit />}
                          onClick={() => {
                            setSelectedDoc(item);
                            setEditDocDialogOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {selectedTab === 6 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Video Guides ({videoGuides.length})
              </Typography>
              <Button variant="contained" startIcon={<Add />}>
                Add Video
              </Button>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Visual tutorials and walkthrough for hosts
            </Typography>

            <Grid container spacing={3}>
              {videoGuides.map((video) => (
                <Grid item xs={12} md={6} lg={4} key={video.id}>
                  <Card>
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={video.thumbnail}
                        alt={video.title}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover'
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: 'white',
                          fontSize: 48
                        }}
                      >
                        <PlayCircle />
                      </Box>
                      <Chip
                        label={video.duration}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white'
                        }}
                      />
                    </Box>
                    <CardContent>
                      <Typography variant="h6" component="div" gutterBottom>
                        {video.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {video.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Chip label={video.category} size="small" variant="outlined" />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Star sx={{ color: '#3b82f6', fontSize: 16 }} />
                          <Typography variant="body2">{video.rating}</Typography>
                        </Box>
                      </Box>

                      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                        {video.views.toLocaleString()} views
                      </Typography>

                      <Button variant="contained" fullWidth startIcon={<PlayCircle />}>
                        Watch Video
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button variant="outlined" size="large" startIcon={<VideoLibrary />}>
                View All Videos
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {selectedTab === 7 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Training Modules ({trainingModules.length + trainingModulesList.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCreateModuleDialogOpen(true)}
              >
                Create Module
              </Button>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Interactive learning experiences for hosts
            </Typography>

            <Grid container spacing={3}>
              {[...trainingModulesList, ...trainingModules].map((module) => (
                <Grid item xs={12} md={6} key={module.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="div" gutterBottom>
                            {module.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {module.description}
                          </Typography>
                        </Box>
                        <Chip 
                          label={module.difficulty} 
                          size="small" 
                          color={
                            module.difficulty === 'Beginner' ? 'success' :
                            module.difficulty === 'Intermediate' ? 'warning' : 'error'
                          }
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2">
                          Completion Rate: {module.completionRate}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {module.duration}
                        </Typography>
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={module.completionRate}
                        sx={{ mb: 2, height: 6, borderRadius: 3 }}
                      />

                      <Chip 
                        label={module.category} 
                        size="small" 
                        variant="outlined" 
                        sx={{ mb: 2 }}
                      />

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" variant="contained" fullWidth>
                          Start Training
                        </Button>
                        <Button size="small" variant="outlined" fullWidth>
                          View Details
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button variant="outlined" size="large" startIcon={<Assignment />}>
                View All Training
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {selectedTab === 8 && (
        <Grid container spacing={3}>
          {/* Engagement Trends */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Engagement Trends
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={engagementTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="engagement" stroke="#2196F3" strokeWidth={2} name="Engagement Rate" />
                    <Line type="monotone" dataKey="completion" stroke="#4CAF50" strokeWidth={2} name="Completion Rate" />
                    <Line type="monotone" dataKey="performance" stroke="#FF9800" strokeWidth={2} name="Performance Lift" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Segment Performance */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Segment Performance
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      dataKey="value"
                      data={segmentPerformanceData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={(entry) => `${entry.name}: ${entry.value}%`}
                    >
                      {segmentPerformanceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Content Type Performance */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Content Type Performance
                </Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={contentTypeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="engagement" fill="#2196F3" name="Engagement Rate %" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 9 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Nudge Effectiveness Metrics
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Last 30 Days</InputLabel>
                  <Select value="30d">
                    <MenuItem value="7d">Last 7 days</MenuItem>
                    <MenuItem value="30d">Last 30 days</MenuItem>
                    <MenuItem value="90d">Last 90 days</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="outlined" startIcon={<Download />}>
                  Export
                </Button>
              </Box>
            </Box>

            {/* Key Metrics */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {nudgeEffectivenessMetrics.map((metric) => (
                <Grid item xs={12} sm={6} md={3} key={metric.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h4" component="div" gutterBottom>
                        {metric.id.includes('Rate') ? `${metric.value}%` : metric.value.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {metric.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {metric.changeDirection === 'up' ? (
                          <TrendingUp sx={{ color: '#4CAF50', fontSize: 16, mr: 0.5 }} />
                        ) : (
                          <TrendingDown sx={{ color: '#F44336', fontSize: 16, mr: 0.5 }} />
                        )}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: metric.changeDirection === 'up' ? '#4CAF50' : '#F44336'
                          }}
                        >
                          {metric.change > 0 ? '+' : ''}{metric.change}%
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {metric.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Performance by Category Chart */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Nudge Performance by Category
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Bar chart showing performance metrics by category
                </Typography>
                <Box sx={{ height: 200, backgroundColor: '#f5f5f5', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    [Performance by Category Chart]
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Host Behavior Improvement Trends */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Host Behavior Improvement Trends
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Line chart showing behavior improvement over time
                </Typography>
                <Box sx={{ height: 200, backgroundColor: '#f5f5f5', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    [Behavior Improvement Trends Chart]
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      )}

      {selectedTab === 10 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Nudge Activity
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Host</TableCell>
                    <TableCell>Nudge Type</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Sent</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nudgeHistory.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {item.hostName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.hostType} • {item.hostId}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.nudgeType}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(item.sentAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            icon={item.opened ? <CheckCircle /> : <Cancel />}
                            label="Opened"
                            size="small"
                            color={item.opened ? 'success' : 'default'}
                            variant="outlined"
                          />
                          <Chip
                            icon={item.clicked ? <CheckCircle /> : <Cancel />}
                            label="Clicked"
                            size="small"
                            color={item.clicked ? 'success' : 'default'}
                            variant="outlined"
                          />
                          <Chip
                            icon={item.converted ? <CheckCircle /> : <Cancel />}
                            label="Converted"
                            size="small"
                            color={item.converted ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          color={item.score >= 7 ? 'success.main' : item.score >= 4 ? 'warning.main' : 'error.main'}
                        >
                          {item.score}/10
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {selectedTab === 11 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Export Education Data
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Export campaigns, content, segments, and metrics data in various formats
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              {/* Export Configuration */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                      Export Configuration
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Data Type</InputLabel>
                      <Select
                        value={exportDataType}
                        onChange={(e) => setExportDataType(e.target.value as any)}
                        label="Data Type"
                      >
                        <MenuItem value="campaigns">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Campaign fontSize="small" />
                            <Typography>Active Campaigns</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="content">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <School fontSize="small" />
                            <Typography>Education Content</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="segments">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Group fontSize="small" />
                            <Typography>Host Segments</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="metrics">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Analytics fontSize="small" />
                            <Typography>Performance Metrics</Typography>
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Export Format</InputLabel>
                      <Select
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value as any)}
                        label="Export Format"
                      >
                        <MenuItem value="csv">CSV (.csv)</MenuItem>
                        <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                        <MenuItem value="pdf">PDF (.pdf)</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Date Range</InputLabel>
                      <Select
                        value={exportDateRange}
                        onChange={(e) => setExportDateRange(e.target.value)}
                        label="Date Range"
                      >
                        <MenuItem value="7d">Last 7 days</MenuItem>
                        <MenuItem value="30d">Last 30 days</MenuItem>
                        <MenuItem value="90d">Last 90 days</MenuItem>
                        <MenuItem value="all">All time</MenuItem>
                      </Select>
                    </FormControl>

                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={isExporting ? null : <Download />}
                      onClick={handleExportData}
                      disabled={isExporting}
                      size="large"
                    >
                      {isExporting ? 'Exporting...' : 'Export Data'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Export Preview */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                      Export Preview
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    {exportDataType === 'campaigns' && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          This export will include:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Chip label={`${activeCampaigns.length} campaigns`} size="small" color="primary" variant="outlined" />
                          <Typography variant="caption" color="text.secondary">
                            • Campaign name, type, and status
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Target audience and send method
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Delivery metrics (delivered, opened, clicked, converted)
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Calculated rates (open rate, click rate, conversion rate)
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {exportDataType === 'content' && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          This export will include:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Chip label={`${educationContent.length} content items`} size="small" color="primary" variant="outlined" />
                          <Typography variant="caption" color="text.secondary">
                            • Content title, type, and category
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Duration and completion rate
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Rating and view count
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Status and creation date
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {exportDataType === 'segments' && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          This export will include:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Chip label={`${hostSegments.length} segments`} size="small" color="primary" variant="outlined" />
                          <Typography variant="caption" color="text.secondary">
                            • Segment name and criteria
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Host count
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Average performance metrics
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Segment description
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {exportDataType === 'metrics' && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          This export will include:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Chip label={`${nudgeMetrics.length} metrics`} size="small" color="primary" variant="outlined" />
                          <Typography variant="caption" color="text.secondary">
                            • Metric title and current value
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Change percentage and direction
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Metric description
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    <Alert severity="info" sx={{ mt: 3 }}>
                      <AlertTitle>Export Note</AlertTitle>
                      The exported file will be downloaded to your default downloads folder.
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>

              {/* Quick Actions */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                      Quick Export Templates
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<Campaign />}
                          onClick={() => {
                            setExportDataType('campaigns');
                            setExportFormat('csv');
                          }}
                        >
                          Campaign Report
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<School />}
                          onClick={() => {
                            setExportDataType('content');
                            setExportFormat('csv');
                          }}
                        >
                          Content Library
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<Group />}
                          onClick={() => {
                            setExportDataType('segments');
                            setExportFormat('csv');
                          }}
                        >
                          Segment Analysis
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<Analytics />}
                          onClick={() => {
                            setExportDataType('metrics');
                            setExportFormat('csv');
                          }}
                        >
                          Performance Metrics
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      {/* Create Custom Nudge Dialog */}
      <Dialog open={createNudgeDialogOpen} onClose={() => setCreateNudgeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Custom Nudge</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a custom triggered nudge that will automatically send educational content to hosts based on specific conditions.
          </Typography>

          <Grid container spacing={2}>
            {/* Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nudge Title"
                value={customNudgeData.title}
                onChange={(e) => setCustomNudgeData({ ...customNudgeData, title: e.target.value })}
                placeholder="e.g., Low Rating Alert"
                required
                helperText="A short, descriptive title for this nudge"
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={customNudgeData.description}
                onChange={(e) => setCustomNudgeData({ ...customNudgeData, description: e.target.value })}
                placeholder="e.g., Triggered when host rating drops below 4.0"
                helperText="Brief explanation of when this nudge triggers"
              />
            </Grid>

            {/* Trigger Condition */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trigger Condition"
                value={customNudgeData.triggerCondition}
                onChange={(e) => setCustomNudgeData({ ...customNudgeData, triggerCondition: e.target.value })}
                placeholder="e.g., Host rating < 4.0 for 7 consecutive days"
                required
                helperText="Specific condition that triggers this nudge"
              />
            </Grid>

            {/* Message Content */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message Content"
                value={customNudgeData.messageContent}
                onChange={(e) => setCustomNudgeData({ ...customNudgeData, messageContent: e.target.value })}
                placeholder="Write the message that will be sent to hosts when this nudge triggers..."
                required
                helperText="The educational message hosts will receive"
              />
            </Grid>

            {/* Call to Action */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Call to Action (CTA)"
                value={customNudgeData.cta}
                onChange={(e) => setCustomNudgeData({ ...customNudgeData, cta: e.target.value })}
                placeholder="e.g., Improve Your Rating, View Tips"
                helperText="Button text for host action (default: Learn More)"
              />
            </Grid>

            {/* Severity and Impact */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={customNudgeData.severity}
                  onChange={(e) => setCustomNudgeData({ ...customNudgeData, severity: e.target.value as any })}
                  label="Severity"
                >
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Impact Level</InputLabel>
                <Select
                  value={customNudgeData.impact}
                  onChange={(e) => setCustomNudgeData({ ...customNudgeData, impact: e.target.value })}
                  label="Impact Level"
                >
                  <MenuItem value="Low Impact">Low Impact</MenuItem>
                  <MenuItem value="Medium Impact">Medium Impact</MenuItem>
                  <MenuItem value="High Impact">High Impact</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Auto-enabled */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={customNudgeData.autoEnabled}
                    onChange={(e) => setCustomNudgeData({ ...customNudgeData, autoEnabled: e.target.checked })}
                  />
                }
                label="Enable automatic triggering"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -1 }}>
                When enabled, this nudge will automatically trigger when conditions are met
              </Typography>
            </Grid>

            {/* Preview */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Nudge Preview
              </Typography>
              <Alert severity={customNudgeData.severity}>
                <AlertTitle>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{customNudgeData.title || 'Nudge Title'}</span>
                    <Chip
                      label={customNudgeData.impact}
                      size="small"
                      color={customNudgeData.impact.includes('High') ? 'error' : customNudgeData.impact.includes('Medium') ? 'warning' : 'success'}
                    />
                  </Box>
                </AlertTitle>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {customNudgeData.messageContent || 'Your nudge message will appear here...'}
                </Typography>
                <Button variant="contained" size="small">
                  {customNudgeData.cta || 'Learn More'}
                </Button>
              </Alert>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                <strong>Trigger:</strong> {customNudgeData.triggerCondition || 'Not specified'} • <strong>Auto-enabled:</strong> {customNudgeData.autoEnabled ? 'Yes' : 'No'}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateNudgeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCustomNudge} startIcon={<Add />}>
            Create Nudge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Triggered Tip Dialog */}
      <Dialog open={editTipDialogOpen} onClose={() => setEditTipDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Triggered Tip</DialogTitle>
        <DialogContent>
          {selectedTip && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Edit the configuration and settings for this triggered tip.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tip Title"
                    value={selectedTip.title}
                    onChange={(e) => setSelectedTip({ ...selectedTip, title: e.target.value })}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={selectedTip.description}
                    onChange={(e) => setSelectedTip({ ...selectedTip, description: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Trigger Condition"
                    value={selectedTip.triggerCondition}
                    onChange={(e) => setSelectedTip({ ...selectedTip, triggerCondition: e.target.value })}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Message Content"
                    value={selectedTip.messageContent}
                    onChange={(e) => setSelectedTip({ ...selectedTip, messageContent: e.target.value })}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Call to Action"
                    value={selectedTip.cta}
                    onChange={(e) => setSelectedTip({ ...selectedTip, cta: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Severity</InputLabel>
                    <Select
                      value={selectedTip.severity}
                      onChange={(e) => setSelectedTip({ ...selectedTip, severity: e.target.value as any })}
                      label="Severity"
                    >
                      <MenuItem value="info">Info</MenuItem>
                      <MenuItem value="success">Success</MenuItem>
                      <MenuItem value="warning">Warning</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Impact Level</InputLabel>
                    <Select
                      value={selectedTip.impact}
                      onChange={(e) => setSelectedTip({ ...selectedTip, impact: e.target.value })}
                      label="Impact Level"
                    >
                      <MenuItem value="Low Impact">Low Impact</MenuItem>
                      <MenuItem value="Medium Impact">Medium Impact</MenuItem>
                      <MenuItem value="High Impact">High Impact</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedTip.autoEnabled}
                        onChange={(e) => setSelectedTip({ ...selectedTip, autoEnabled: e.target.checked })}
                      />
                    }
                    label="Auto-enabled"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Current Statistics
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Box>
                        <Typography variant="h6">{selectedTip.currentTriggers}</Typography>
                        <Typography variant="caption" color="text.secondary">Current Triggers</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2">{selectedTip.lastTriggered}</Typography>
                        <Typography variant="caption" color="text.secondary">Last Triggered</Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTipDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTipEdit} startIcon={<Edit />}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tip Analytics Dialog */}
      <Dialog open={tipAnalyticsDialogOpen} onClose={() => setTipAnalyticsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Triggered Tip Analytics</DialogTitle>
        <DialogContent>
          {selectedTip && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>{selectedTip.title}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedTip.description}</Typography>
              </Box>

              <Grid container spacing={3}>
                {/* Key Metrics */}
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Total Triggers</Typography>
                      <Typography variant="h4">{selectedTip.currentTriggers}</Typography>
                      <Chip label={selectedTip.impact} size="small" color={selectedTip.impact.includes('High') ? 'error' : selectedTip.impact.includes('Medium') ? 'warning' : 'success'} sx={{ mt: 1 }} />
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Last Triggered</Typography>
                      <Typography variant="h5">{selectedTip.lastTriggered}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>ago</Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Engagement Rate</Typography>
                      <Typography variant="h4">67.3%</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <TrendingUp sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                        <Typography variant="caption" color="success.main">+8.2%</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Typography variant="h6">{selectedTip.autoEnabled ? 'Active' : 'Inactive'}</Typography>
                      <Chip
                        label={selectedTip.autoEnabled ? 'Auto-enabled' : 'Disabled'}
                        size="small"
                        color={selectedTip.autoEnabled ? 'success' : 'default'}
                        sx={{ mt: 1 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Trigger Performance */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Trigger Performance Over Time</Typography>
                      <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          [Line chart showing trigger frequency over the last 30 days]
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Host Impact Analysis */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Host Segments Affected</Typography>
                      <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          [Pie chart showing distribution across host segments]
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Response Metrics</Typography>
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Click-through Rate</Typography>
                            <Typography variant="body2" fontWeight="medium">45.2%</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={45} />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Action Completion</Typography>
                            <Typography variant="body2" fontWeight="medium">32.8%</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={33} />
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Behavior Improvement</Typography>
                            <Typography variant="body2" fontWeight="medium">58.6%</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={59} color="success" />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Trigger Configuration */}
                <Grid item xs={12}>
                  <Alert severity={selectedTip.severity}>
                    <AlertTitle>Current Trigger Configuration</AlertTitle>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Condition:</strong> {selectedTip.triggerCondition}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Message:</strong> {selectedTip.messageContent}
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTipAnalyticsDialogOpen(false)}>Close</Button>
          <Button variant="outlined" startIcon={<Download />}>
            Export Analytics
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={campaignDialogOpen} onClose={() => setCampaignDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Campaign</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set up a new educational nudge campaign to send awareness to host segments.
          </Typography>

          <Grid container spacing={2}>
            {/* Campaign Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={campaignData.name}
                onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                placeholder="e.g., New Host Welcome Series"
                required
                helperText="A descriptive name for this campaign"
              />
            </Grid>

            {/* Campaign Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Campaign Type</InputLabel>
                <Select
                  value={campaignData.type}
                  onChange={(e) => setCampaignData({ ...campaignData, type: e.target.value as any })}
                  label="Campaign Type"
                >
                  <MenuItem value="onboarding">Onboarding</MenuItem>
                  <MenuItem value="performance">Performance Improvement</MenuItem>
                  <MenuItem value="engagement">Host Engagement</MenuItem>
                  <MenuItem value="compliance">Compliance & Safety</MenuItem>
                  <MenuItem value="feature">Feature Announcement</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Target Audience */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Target Audience</InputLabel>
                <Select
                  value={campaignData.targetAudience}
                  onChange={(e) => setCampaignData({ ...campaignData, targetAudience: e.target.value })}
                  label="Target Audience"
                >
                  <MenuItem value="all-hosts">All Hosts</MenuItem>
                  <MenuItem value="new-hosts">New Hosts (0-30 days)</MenuItem>
                  <MenuItem value="low-performers">Low Performing Hosts</MenuItem>
                  <MenuItem value="high-performers">High Performing Hosts</MenuItem>
                  <MenuItem value="inactive-hosts">Inactive Hosts</MenuItem>
                  <MenuItem value="fleet-managers">Fleet Managers</MenuItem>
                  <MenuItem value="owners">Vehicle Owners</MenuItem>
                  <MenuItem value="brokers">Brokers</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Message */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Campaign Message"
                value={campaignData.message}
                onChange={(e) => setCampaignData({ ...campaignData, message: e.target.value })}
                placeholder="Write your awareness message to hosts here..."
                required
                helperText="The main message that will be sent to hosts"
              />
            </Grid>

            {/* Call to Action */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Call to Action (CTA)"
                value={campaignData.cta}
                onChange={(e) => setCampaignData({ ...campaignData, cta: e.target.value })}
                placeholder="e.g., Complete Your Profile, Learn More, Get Started"
                helperText="Optional action button text"
              />
            </Grid>

            {/* Send Method */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Send Method</InputLabel>
                <Select
                  value={campaignData.sendMethod}
                  onChange={(e) => setCampaignData({ ...campaignData, sendMethod: e.target.value as any })}
                  label="Send Method"
                >
                  <MenuItem value="email">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotificationsActive fontSize="small" />
                      <Typography>Email</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="sms">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotificationsActive fontSize="small" />
                      <Typography>SMS</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="push">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotificationsActive fontSize="small" />
                      <Typography>Push Notification</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="in-app">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotificationsActive fontSize="small" />
                      <Typography>In-App Message</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Send Time */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Send Time</InputLabel>
                <Select
                  value={campaignData.sendTime}
                  onChange={(e) => setCampaignData({ ...campaignData, sendTime: e.target.value })}
                  label="Send Time"
                >
                  <MenuItem value="immediate">Send Immediately</MenuItem>
                  <MenuItem value="scheduled">Schedule for Later</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Scheduled Date (conditional) */}
            {campaignData.sendTime === 'scheduled' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label="Scheduled Date"
                  value={campaignData.scheduledDate}
                  onChange={(e) => setCampaignData({ ...campaignData, scheduledDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  helperText="Select when the campaign should be sent"
                />
              </Grid>
            )}

            {/* Preview Card */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Campaign Preview
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip
                      label={campaignData.type}
                      size="small"
                      color="primary"
                      sx={{ textTransform: 'capitalize' }}
                    />
                    <Chip
                      label={campaignData.sendMethod}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1, textTransform: 'capitalize' }}
                    />
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {campaignData.name || 'Campaign Name'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {campaignData.message || 'Your campaign message will appear here...'}
                  </Typography>
                  {campaignData.cta && (
                    <Button variant="contained" size="small">
                      {campaignData.cta}
                    </Button>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    <strong>Target:</strong> {campaignData.targetAudience} • <strong>Send:</strong> {campaignData.sendTime === 'immediate' ? 'Immediately' : campaignData.scheduledDate || 'Not scheduled'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCampaign} startIcon={<Campaign />}>
            Create Campaign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={abTestDialogOpen} onClose={() => setAbTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>A/B Test Configuration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Configure A/B test parameters for campaign optimization.
          </Typography>
          {/* A/B test configuration would go here */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbTestDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Start A/B Test</Button>
        </DialogActions>
      </Dialog>

      {/* Add Content Dialog */}
      <Dialog open={addContentDialogOpen} onClose={() => setAddContentDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Educational Content</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add new educational content to the library for host training and development.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content Title"
                value={newContent.title}
                onChange={(e) => setNewContent({ ...newContent, title: e.target.value })}
                placeholder="e.g., Advanced Host Strategies"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Content Type</InputLabel>
                <Select
                  value={newContent.type}
                  onChange={(e) => setNewContent({ ...newContent, type: e.target.value as any })}
                  label="Content Type"
                >
                  <MenuItem value="video">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VideoLibrary fontSize="small" />
                      Video
                    </Box>
                  </MenuItem>
                  <MenuItem value="article">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Article fontSize="small" />
                      Article
                    </Box>
                  </MenuItem>
                  <MenuItem value="quiz">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Quiz fontSize="small" />
                      Quiz
                    </Box>
                  </MenuItem>
                  <MenuItem value="checklist">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Assignment fontSize="small" />
                      Checklist
                    </Box>
                  </MenuItem>
                  <MenuItem value="webinar">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PlayCircle fontSize="small" />
                      Webinar
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newContent.category}
                  onChange={(e) => setNewContent({ ...newContent, category: e.target.value as any })}
                  label="Category"
                >
                  <MenuItem value="onboarding">Onboarding</MenuItem>
                  <MenuItem value="optimization">Optimization</MenuItem>
                  <MenuItem value="safety">Safety</MenuItem>
                  <MenuItem value="compliance">Compliance</MenuItem>
                  <MenuItem value="growth">Growth</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration"
                value={newContent.duration}
                onChange={(e) => setNewContent({ ...newContent, duration: e.target.value })}
                placeholder="e.g., 25 min or 8 min read"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Content URL"
                value={newContent.url}
                onChange={(e) => setNewContent({ ...newContent, url: e.target.value })}
                placeholder="https://..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={newContent.description}
                onChange={(e) => setNewContent({ ...newContent, description: e.target.value })}
                placeholder="Brief description of the content and learning objectives..."
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            <AlertTitle>Content Guidelines</AlertTitle>
            <Typography variant="body2">
              • Ensure content is relevant and valuable for host education<br />
              • Videos should be high-quality and professionally produced<br />
              • Articles should be well-structured with clear takeaways<br />
              • Quizzes should test understanding of key concepts
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddContentDialogOpen(false);
            setNewContent({
              title: '',
              type: 'video',
              category: 'onboarding',
              duration: '',
              url: '',
              description: ''
            });
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              // Here you would normally call an API to create the content

              // For now, just show a success message and close the dialog
              alert(`Content "${newContent.title}" has been added successfully!\n\nType: ${newContent.type}\nCategory: ${newContent.category}\nDuration: ${newContent.duration}`);

              setAddContentDialogOpen(false);
              setNewContent({
                title: '',
                type: 'video',
                category: 'onboarding',
                duration: '',
                url: '',
                description: ''
              });
            }}
            disabled={!newContent.title || !newContent.duration}
          >
            Add Content
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Content Dialog */}
      <Dialog open={editContentDialogOpen} onClose={() => setEditContentDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Educational Content</DialogTitle>
        <DialogContent>
          {selectedContent && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Update the details for "{selectedContent.title}"
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Content Title"
                    defaultValue={selectedContent.title}
                    placeholder="e.g., Advanced Host Strategies"
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Content Type</InputLabel>
                    <Select
                      defaultValue={selectedContent.type}
                      label="Content Type"
                    >
                      <MenuItem value="video">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VideoLibrary fontSize="small" />
                          Video
                        </Box>
                      </MenuItem>
                      <MenuItem value="article">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Article fontSize="small" />
                          Article
                        </Box>
                      </MenuItem>
                      <MenuItem value="quiz">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Quiz fontSize="small" />
                          Quiz
                        </Box>
                      </MenuItem>
                      <MenuItem value="checklist">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Assignment fontSize="small" />
                          Checklist
                        </Box>
                      </MenuItem>
                      <MenuItem value="webinar">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PlayCircle fontSize="small" />
                          Webinar
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Category</InputLabel>
                    <Select
                      defaultValue={selectedContent.category}
                      label="Category"
                    >
                      <MenuItem value="onboarding">Onboarding</MenuItem>
                      <MenuItem value="optimization">Optimization</MenuItem>
                      <MenuItem value="safety">Safety</MenuItem>
                      <MenuItem value="compliance">Compliance</MenuItem>
                      <MenuItem value="growth">Growth</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Duration"
                    defaultValue={selectedContent.duration}
                    placeholder="e.g., 25 min or 8 min read"
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      defaultValue={selectedContent.status}
                      label="Status"
                    >
                      <MenuItem value="published">
                        <Chip label="Published" size="small" color="success" />
                      </MenuItem>
                      <MenuItem value="draft">
                        <Chip label="Draft" size="small" color="default" />
                      </MenuItem>
                      <MenuItem value="archived">
                        <Chip label="Archived" size="small" color="warning" />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Description"
                    placeholder="Brief description of the content and learning objectives..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info">
                    <AlertTitle>Current Stats</AlertTitle>
                    <Typography variant="body2">
                      • Completion Rate: {selectedContent.completionRate}%<br />
                      • Rating: {selectedContent.rating}/5<br />
                      • Total Views: {selectedContent.views.toLocaleString()}<br />
                      • Created: {new Date(selectedContent.createdAt).toLocaleDateString()}
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditContentDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => {
              alert(`Content "${selectedContent?.title}" has been updated successfully!`);
              setEditContentDialogOpen(false);
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={analyticsDialogOpen} onClose={() => setAnalyticsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Content Analytics: {selectedContent?.title}
        </DialogTitle>
        <DialogContent>
          {selectedContent && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Detailed analytics and performance metrics for this educational content
              </Typography>

              {/* Key Metrics */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Total Views
                      </Typography>
                      <Typography variant="h4" sx={{ my: 1 }}>
                        {selectedContent.views.toLocaleString()}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUp sx={{ color: '#4CAF50', fontSize: 16, mr: 0.5 }} />
                        <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                          +12.5% this month
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Completion Rate
                      </Typography>
                      <Typography variant="h4" sx={{ my: 1 }}>
                        {selectedContent.completionRate}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={selectedContent.completionRate}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Average Rating
                      </Typography>
                      <Typography variant="h4" sx={{ my: 1 }}>
                        {selectedContent.rating}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Star sx={{ color: '#FFC107', fontSize: 16, mr: 0.5 }} />
                        <Star sx={{ color: '#FFC107', fontSize: 16, mr: 0.5 }} />
                        <Star sx={{ color: '#FFC107', fontSize: 16, mr: 0.5 }} />
                        <Star sx={{ color: '#FFC107', fontSize: 16, mr: 0.5 }} />
                        <Star sx={{ color: selectedContent.rating >= 4.5 ? '#FFC107' : '#E0E0E0', fontSize: 16 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Engagement Score
                      </Typography>
                      <Typography variant="h4" sx={{ my: 1, color: '#2196F3' }}>
                        87
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Above average
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Views Trend Chart */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Views Trend (Last 30 Days)
                  </Typography>
                  <Box sx={{ height: 200, backgroundColor: '#f5f5f5', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      [Line Chart: Views over time]
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Audience Demographics */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Audience Breakdown
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">New Hosts</Typography>
                          <Typography variant="body2" fontWeight="medium">45%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={45} sx={{ mb: 2, height: 6, borderRadius: 3 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Experienced Hosts</Typography>
                          <Typography variant="body2" fontWeight="medium">35%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={35} sx={{ mb: 2, height: 6, borderRadius: 3 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Fleet Managers</Typography>
                          <Typography variant="body2" fontWeight="medium">20%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={20} sx={{ height: 6, borderRadius: 3 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Performance Impact
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Alert severity="success" sx={{ mb: 2 }}>
                          <AlertTitle>Host Performance Improvement</AlertTitle>
                          Hosts who completed this content showed a 23% improvement in their performance metrics.
                        </Alert>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Booking Rate Increase</Typography>
                          <Typography variant="body2" fontWeight="medium" color="success.main">+18%</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Rating Improvement</Typography>
                          <Typography variant="body2" fontWeight="medium" color="success.main">+0.4</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Response Time</Typography>
                          <Typography variant="body2" fontWeight="medium" color="success.main">-32%</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Feedback & Comments */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Recent Feedback
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium">John Smith</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star key={star} sx={{ color: '#FFC107', fontSize: 16 }} />
                          ))}
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        "Excellent content! Really helped me improve my listing quality and booking rates."
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        2 days ago
                      </Typography>
                    </Box>

                    <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium">Sarah Johnson</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {[1, 2, 3, 4].map((star) => (
                            <Star key={star} sx={{ color: '#FFC107', fontSize: 16 }} />
                          ))}
                          <Star sx={{ color: '#E0E0E0', fontSize: 16 }} />
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        "Very informative. Would love to see more advanced topics covered."
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        5 days ago
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsDialogOpen(false)}>
            Close
          </Button>
          <Button variant="outlined" startIcon={<Download />}>
            Export Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog open={createTemplateDialogOpen} onClose={() => setCreateTemplateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Nudge Template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a reusable nudge template that can be triggered based on specific conditions
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Template Name"
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                placeholder="e.g., Welcome New Host"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newTemplate.category}
                  onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="Onboarding">Onboarding</MenuItem>
                  <MenuItem value="Performance">Performance</MenuItem>
                  <MenuItem value="Growth">Growth</MenuItem>
                  <MenuItem value="Engagement">Engagement</MenuItem>
                  <MenuItem value="Compliance">Compliance</MenuItem>
                  <MenuItem value="Feature">Feature</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Send Method</InputLabel>
                <Select
                  value={newTemplate.sendMethod}
                  onChange={(e) => setNewTemplate({ ...newTemplate, sendMethod: e.target.value as any })}
                  label="Send Method"
                >
                  <MenuItem value="email">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Campaign fontSize="small" />
                      Email
                    </Box>
                  </MenuItem>
                  <MenuItem value="sms">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Campaign fontSize="small" />
                      SMS
                    </Box>
                  </MenuItem>
                  <MenuItem value="push">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotificationsActive fontSize="small" />
                      Push Notification
                    </Box>
                  </MenuItem>
                  <MenuItem value="in-app">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Campaign fontSize="small" />
                      In-App Message
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trigger Condition"
                value={newTemplate.trigger}
                onChange={(e) => setNewTemplate({ ...newTemplate, trigger: e.target.value })}
                placeholder="e.g., Account created, Rating drops below 4.0"
                required
                helperText="When should this nudge be triggered?"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Target Audience</InputLabel>
                <Select
                  value={newTemplate.targetAudience}
                  onChange={(e) => setNewTemplate({ ...newTemplate, targetAudience: e.target.value })}
                  label="Target Audience"
                >
                  <MenuItem value="all-hosts">All Hosts</MenuItem>
                  <MenuItem value="new-hosts">New Hosts (0-30 days)</MenuItem>
                  <MenuItem value="low-performers">Low Performers</MenuItem>
                  <MenuItem value="high-performers">High Performers</MenuItem>
                  <MenuItem value="inactive-hosts">Inactive Hosts</MenuItem>
                  <MenuItem value="fleet-managers">Fleet Managers</MenuItem>
                  <MenuItem value="brokers">Brokers</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nudge Title"
                value={newTemplate.title}
                onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })}
                placeholder="e.g., Welcome to PolarRide! Let's get you started"
                required
                helperText="The main headline hosts will see"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Nudge Message"
                value={newTemplate.message}
                onChange={(e) => setNewTemplate({ ...newTemplate, message: e.target.value })}
                placeholder="Complete your profile and list your first vehicle to start earning..."
                required
                helperText="The main body text of your nudge"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Call to Action (CTA)"
                value={newTemplate.cta}
                onChange={(e) => setNewTemplate({ ...newTemplate, cta: e.target.value })}
                placeholder="e.g., Complete Setup, View Tips, Learn More"
                required
                helperText="The text for the action button"
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            <AlertTitle>Template Best Practices</AlertTitle>
            <Typography variant="body2">
              • Keep messages concise and action-oriented<br />
              • Use clear, compelling CTAs that drive engagement<br />
              • Ensure triggers are specific and measurable<br />
              • Test effectiveness before rolling out to large audiences<br />
              • Personalize messages when possible
            </Typography>
          </Alert>

          {/* Preview Box */}
          {(newTemplate.title || newTemplate.message) && (
            <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#f5f5f5' }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Preview:
              </Typography>
              {newTemplate.title && (
                <Typography variant="h6" gutterBottom>
                  {newTemplate.title}
                </Typography>
              )}
              {newTemplate.message && (
                <Typography variant="body2" color="text.secondary" paragraph>
                  {newTemplate.message}
                </Typography>
              )}
              {newTemplate.cta && (
                <Button variant="contained" size="small">
                  {newTemplate.cta}
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateTemplateDialogOpen(false);
            setNewTemplate({
              name: '',
              category: 'Onboarding',
              trigger: '',
              title: '',
              message: '',
              cta: '',
              targetAudience: 'all-hosts',
              sendMethod: 'email',
            });
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              alert(`Template "${newTemplate.name}" has been created successfully!\n\nCategory: ${newTemplate.category}\nTrigger: ${newTemplate.trigger}\nTarget: ${newTemplate.targetAudience}\nSend Method: ${newTemplate.sendMethod}`);
              setCreateTemplateDialogOpen(false);
              setNewTemplate({
                name: '',
                category: 'Onboarding',
                trigger: '',
                title: '',
                message: '',
                cta: '',
                targetAudience: 'all-hosts',
                sendMethod: 'email',
              });
            }}
            disabled={!newTemplate.name || !newTemplate.trigger || !newTemplate.title || !newTemplate.message || !newTemplate.cta}
          >
            Create Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={editTemplateDialogOpen} onClose={() => setEditTemplateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Nudge Template</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Update the details for "{selectedTemplate.name}"
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Template Name"
                    defaultValue={selectedTemplate.name}
                    placeholder="e.g., Welcome New Host"
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Category</InputLabel>
                    <Select
                      defaultValue={selectedTemplate.category}
                      label="Category"
                    >
                      <MenuItem value="Onboarding">Onboarding</MenuItem>
                      <MenuItem value="Performance">Performance</MenuItem>
                      <MenuItem value="Growth">Growth</MenuItem>
                      <MenuItem value="Engagement">Engagement</MenuItem>
                      <MenuItem value="Compliance">Compliance</MenuItem>
                      <MenuItem value="Feature">Feature</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Status</InputLabel>
                    <Select
                      defaultValue={selectedTemplate.status}
                      label="Status"
                    >
                      <MenuItem value="active">
                        <Chip label="Active" size="small" color="success" />
                      </MenuItem>
                      <MenuItem value="draft">
                        <Chip label="Draft" size="small" color="default" />
                      </MenuItem>
                      <MenuItem value="archived">
                        <Chip label="Archived" size="small" color="warning" />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Trigger Condition"
                    defaultValue={selectedTemplate.trigger}
                    placeholder="e.g., Account created, Rating drops below 4.0"
                    required
                    helperText="When should this nudge be triggered?"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Target Audience"
                    defaultValue={selectedTemplate.targetAudience}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nudge Title"
                    defaultValue={selectedTemplate.title}
                    required
                    helperText="The main headline hosts will see"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Nudge Message"
                    defaultValue={selectedTemplate.message}
                    required
                    helperText="The main body text of your nudge"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Call to Action (CTA)"
                    defaultValue={selectedTemplate.cta}
                    required
                    helperText="The text for the action button"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info">
                    <AlertTitle>Template Stats</AlertTitle>
                    <Typography variant="body2">
                      • Times Used: {selectedTemplate.timesUsed}<br />
                      • Engagement Rate: {selectedTemplate.engagementRate}%<br />
                      • Conversion Rate: {selectedTemplate.conversionRate}%<br />
                      • Last Used: {formatDate(selectedTemplate.lastUsed)}
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTemplateDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => {
              alert(`Template "${selectedTemplate?.name}" has been updated successfully!`);
              setEditTemplateDialogOpen(false);
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Use Template Dialog */}
      <Dialog open={useTemplateDialogOpen} onClose={() => setUseTemplateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Use Template: {selectedTemplate?.name}</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure how and when to send this nudge to your target audience
              </Typography>

              {/* Template Preview */}
              <Box sx={{ mb: 3, p: 2, border: '2px solid', borderColor: 'primary.main', borderRadius: 2, bgcolor: '#f5f9ff' }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Template Preview:
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                  {selectedTemplate.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {selectedTemplate.message}
                </Typography>
                <Button variant="contained" size="small">
                  {selectedTemplate.cta}
                </Button>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Chip label={selectedTemplate.category} size="small" color="primary" />
                  <Chip label={`Engagement: ${selectedTemplate.engagementRate}%`} size="small" />
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Send To</InputLabel>
                    <Select
                      defaultValue="all-hosts"
                      label="Send To"
                    >
                      <MenuItem value="all-hosts">All Hosts</MenuItem>
                      <MenuItem value="new-hosts">New Hosts (0-30 days)</MenuItem>
                      <MenuItem value="low-performers">Low Performers</MenuItem>
                      <MenuItem value="high-performers">High Performers</MenuItem>
                      <MenuItem value="inactive-hosts">Inactive Hosts</MenuItem>
                      <MenuItem value="fleet-managers">Fleet Managers</MenuItem>
                      <MenuItem value="brokers">Brokers</MenuItem>
                      <MenuItem value="custom">Custom Segment...</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Send Method</InputLabel>
                    <Select
                      defaultValue="email"
                      label="Send Method"
                    >
                      <MenuItem value="email">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Campaign fontSize="small" />
                          Email
                        </Box>
                      </MenuItem>
                      <MenuItem value="sms">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Campaign fontSize="small" />
                          SMS
                        </Box>
                      </MenuItem>
                      <MenuItem value="push">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <NotificationsActive fontSize="small" />
                          Push Notification
                        </Box>
                      </MenuItem>
                      <MenuItem value="in-app">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Campaign fontSize="small" />
                          In-App Message
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Send Time</InputLabel>
                    <Select
                      defaultValue="immediate"
                      label="Send Time"
                    >
                      <MenuItem value="immediate">Send Immediately</MenuItem>
                      <MenuItem value="scheduled">Schedule for Later</MenuItem>
                      <MenuItem value="trigger">Send on Trigger</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Campaign Name"
                    placeholder="e.g., Welcome New Hosts - January 2024"
                    required
                    helperText="Internal name to track this campaign"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Track engagement metrics (opens, clicks, conversions)"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch />}
                    label="Enable A/B testing (send variants to test effectiveness)"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="success">
                    <AlertTitle>Template Performance</AlertTitle>
                    <Typography variant="body2">
                      This template has a {selectedTemplate.engagementRate}% engagement rate and {selectedTemplate.conversionRate}% conversion rate.
                      It has been used {selectedTemplate.timesUsed} times successfully.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUseTemplateDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            startIcon={<Campaign />}
            onClick={() => {
              alert(`Campaign created using template "${selectedTemplate?.name}"!\n\nThe nudge will be sent to your selected audience.`);
              setUseTemplateDialogOpen(false);
            }}
          >
            Send Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Targeted Campaign Dialog */}
      <Dialog open={targetedCampaignDialogOpen} onClose={() => setTargetedCampaignDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Targeted Campaign</DialogTitle>
        <DialogContent>
          {selectedSegment && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create a customized campaign for the <strong>{selectedSegment.name}</strong> segment
              </Typography>

              {/* Segment Info Card */}
              <Card sx={{ mb: 3, bgcolor: '#f5f9ff', border: '2px solid', borderColor: 'primary.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: selectedSegment.color,
                      mr: 2
                    }} />
                    <Typography variant="h6">
                      {selectedSegment.name}
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Target Audience
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {selectedSegment.hostCount.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        hosts
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Avg Performance
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {selectedSegment.avgPerformance}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={selectedSegment.avgPerformance}
                        sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Segment Criteria:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {selectedSegment.criteria.map((criterion, index) => (
                        <Chip
                          key={index}
                          label={criterion}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Campaign Name"
                    value={campaignData.name}
                    onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                    placeholder={`e.g., ${selectedSegment.name} Engagement Campaign - ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
                    required
                    helperText="Internal name to track this campaign"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Campaign Objective</InputLabel>
                    <Select
                      value={campaignData.objective}
                      onChange={(e) => setCampaignData({ ...campaignData, objective: e.target.value })}
                      label="Campaign Objective"
                    >
                      <MenuItem value="engagement">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUp fontSize="small" />
                          Increase Engagement
                        </Box>
                      </MenuItem>
                      <MenuItem value="education">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <School fontSize="small" />
                          Provide Education
                        </Box>
                      </MenuItem>
                      <MenuItem value="performance">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Analytics fontSize="small" />
                          Improve Performance
                        </Box>
                      </MenuItem>
                      <MenuItem value="retention">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle fontSize="small" />
                          Increase Retention
                        </Box>
                      </MenuItem>
                      <MenuItem value="feature-adoption">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Lightbulb fontSize="small" />
                          Feature Adoption
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Send Method</InputLabel>
                    <Select
                      value={campaignData.sendMethod}
                      onChange={(e) => setCampaignData({ ...campaignData, sendMethod: e.target.value as any })}
                      label="Send Method"
                    >
                      <MenuItem value="email">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Campaign fontSize="small" />
                          Email
                        </Box>
                      </MenuItem>
                      <MenuItem value="sms">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Campaign fontSize="small" />
                          SMS
                        </Box>
                      </MenuItem>
                      <MenuItem value="push">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <NotificationsActive fontSize="small" />
                          Push Notification
                        </Box>
                      </MenuItem>
                      <MenuItem value="in-app">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Campaign fontSize="small" />
                          In-App Message
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Send Time</InputLabel>
                    <Select
                      value={campaignData.sendTime}
                      onChange={(e) => setCampaignData({ ...campaignData, sendTime: e.target.value })}
                      label="Send Time"
                    >
                      <MenuItem value="immediate">Send Immediately</MenuItem>
                      <MenuItem value="scheduled">Schedule for Later</MenuItem>
                      <MenuItem value="optimal">Send at Optimal Time</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {campaignData.sendTime === 'scheduled' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="Scheduled Date & Time"
                      value={campaignData.scheduledDate}
                      onChange={(e) => setCampaignData({ ...campaignData, scheduledDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Campaign Message"
                    value={campaignData.message}
                    onChange={(e) => setCampaignData({ ...campaignData, message: e.target.value })}
                    placeholder="Write your message to this segment... Be specific and actionable."
                    required
                    helperText="Keep it concise and relevant to the segment's characteristics"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Call to Action (CTA)"
                    value={campaignData.cta}
                    onChange={(e) => setCampaignData({ ...campaignData, cta: e.target.value })}
                    placeholder="e.g., Improve Your Rating, Complete Training, View Tips"
                    required
                    helperText="Clear action you want hosts to take"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Enable engagement tracking (opens, clicks, conversions)"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch />}
                    label="Send follow-up messages based on engagement"
                  />
                </Grid>
              </Grid>

              {/* Preview */}
              {(campaignData.message || campaignData.cta) && (
                <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#f5f5f5' }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Message Preview:
                  </Typography>
                  {campaignData.message && (
                    <Typography variant="body2" paragraph sx={{ mt: 1 }}>
                      {campaignData.message}
                    </Typography>
                  )}
                  {campaignData.cta && (
                    <Button variant="contained" size="small">
                      {campaignData.cta}
                    </Button>
                  )}
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 3 }}>
                <AlertTitle>Campaign Reach</AlertTitle>
                <Typography variant="body2">
                  This campaign will reach <strong>{selectedSegment.hostCount.toLocaleString()}</strong> hosts in the {selectedSegment.name} segment.
                  Based on segment performance ({selectedSegment.avgPerformance}%), we estimate a {Math.round(selectedSegment.avgPerformance * 0.6)}% engagement rate.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setTargetedCampaignDialogOpen(false);
            setCampaignData({
              name: '',
              objective: 'engagement',
              message: '',
              cta: '',
              sendMethod: 'email',
              sendTime: 'immediate',
              scheduledDate: '',
            });
          }}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            startIcon={<Campaign />}
            onClick={() => {
              alert(`Campaign "${campaignData.name}" created successfully!\n\nTarget: ${selectedSegment?.name}\nAudience: ${selectedSegment?.hostCount.toLocaleString()} hosts\nObjective: ${campaignData.objective}\nSend Method: ${campaignData.sendMethod}\nSend Time: ${campaignData.sendTime}`);
              setTargetedCampaignDialogOpen(false);
              setCampaignData({
                name: '',
                objective: 'engagement',
                message: '',
                cta: '',
                sendMethod: 'email',
                sendTime: 'immediate',
                scheduledDate: '',
              });
            }}
            disabled={!campaignData.name || !campaignData.message || !campaignData.cta}
          >
            Create Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Campaign Dialog */}
      <Dialog open={editCampaignDialogOpen} onClose={() => setEditCampaignDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Campaign</DialogTitle>
        <DialogContent>
          {selectedCampaign && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Update the details for "{selectedCampaign.name}"
              </Typography>

              {/* Campaign Stats Overview */}
              <Card sx={{ mb: 3, bgcolor: '#f5f9ff', border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Current Performance
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">
                        Delivered
                      </Typography>
                      <Typography variant="h6">
                        {selectedCampaign.delivered.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">
                        Opened
                      </Typography>
                      <Typography variant="h6">
                        {((selectedCampaign.opened / selectedCampaign.delivered) * 100).toFixed(1)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">
                        CTR
                      </Typography>
                      <Typography variant="h6">
                        {calculateClickRate(selectedCampaign).toFixed(1)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">
                        Conversions
                      </Typography>
                      <Typography variant="h6">
                        {calculateConversionRate(selectedCampaign).toFixed(1)}%
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Campaign Name"
                    defaultValue={selectedCampaign.name}
                    placeholder="e.g., Welcome New Hosts Campaign"
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Campaign Type</InputLabel>
                    <Select
                      defaultValue={selectedCampaign.type}
                      label="Campaign Type"
                    >
                      <MenuItem value="onboarding">
                        <Chip label="Onboarding" size="small" />
                      </MenuItem>
                      <MenuItem value="performance">
                        <Chip label="Performance" size="small" />
                      </MenuItem>
                      <MenuItem value="engagement">
                        <Chip label="Engagement" size="small" />
                      </MenuItem>
                      <MenuItem value="compliance">
                        <Chip label="Compliance" size="small" />
                      </MenuItem>
                      <MenuItem value="feature">
                        <Chip label="Feature" size="small" />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Status</InputLabel>
                    <Select
                      defaultValue={selectedCampaign.status}
                      label="Status"
                    >
                      <MenuItem value="active">
                        <Chip label="Active" size="small" color="success" />
                      </MenuItem>
                      <MenuItem value="paused">
                        <Chip label="Paused" size="small" color="warning" />
                      </MenuItem>
                      <MenuItem value="draft">
                        <Chip label="Draft" size="small" color="default" />
                      </MenuItem>
                      <MenuItem value="completed">
                        <Chip label="Completed" size="small" color="info" />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Target Audience"
                    defaultValue={selectedCampaign.targetAudience}
                    required
                    helperText="Description of who will receive this campaign"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Send Method</InputLabel>
                    <Select
                      defaultValue={selectedCampaign.sendMethod}
                      label="Send Method"
                    >
                      <MenuItem value="email">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Campaign fontSize="small" />
                          Email
                        </Box>
                      </MenuItem>
                      <MenuItem value="sms">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Campaign fontSize="small" />
                          SMS
                        </Box>
                      </MenuItem>
                      <MenuItem value="push">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <NotificationsActive fontSize="small" />
                          Push Notification
                        </Box>
                      </MenuItem>
                      <MenuItem value="in-app">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Campaign fontSize="small" />
                          In-App Message
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    defaultValue={selectedCampaign.endDate || ''}
                    InputLabelProps={{ shrink: true }}
                    helperText="Optional - when to automatically end this campaign"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Audience Segments
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedCampaign.segments.map((segment, index) => (
                        <Chip
                          key={index}
                          label={segment}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                </Grid>

                {selectedCampaign.abTestVariant && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <AlertTitle>A/B Test Variant {selectedCampaign.abTestVariant}</AlertTitle>
                      <Typography variant="body2">
                        This is variant {selectedCampaign.abTestVariant} of an A/B test. Changes may affect test results.
                      </Typography>
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch defaultChecked={selectedCampaign.status === 'active'} />}
                    label="Campaign Active"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Send follow-up messages based on engagement"
                  />
                </Grid>
              </Grid>

              <Alert severity="warning" sx={{ mt: 3 }}>
                <AlertTitle>Important</AlertTitle>
                <Typography variant="body2">
                  Changes to active campaigns will affect ongoing delivery. Consider pausing the campaign before making major changes.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCampaignDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<Analytics />}
            onClick={() => {
              setEditCampaignDialogOpen(false);
              // Could open analytics dialog here
              alert(`Viewing analytics for "${selectedCampaign?.name}"`);
            }}
          >
            View Analytics
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => {
              alert(`Campaign "${selectedCampaign?.name}" has been updated successfully!\n\nThe changes will take effect immediately.`);
              setEditCampaignDialogOpen(false);
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vehicle Maintenance Dialog */}
      <Dialog
        open={vehicleMaintenanceDialogOpen}
        onClose={() => setVehicleMaintenanceDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CarRepair sx={{ color: 'primary.main' }} />
            <Typography variant="h6" component="span">
              Vehicle Maintenance Tips
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Reduce Substitutions & Improve Guest Experience</AlertTitle>
            Proper vehicle maintenance helps prevent last-minute substitutions, improves your reputation, and maximizes your earnings.
          </Alert>

          {/* Maintenance Checklist */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            Essential Maintenance Checklist
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CheckCircle sx={{ color: 'success.main' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Daily Checks
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Vehicle cleanliness (interior & exterior)
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Fuel level and tire pressure
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Dashboard warning lights
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Lights, wipers, and signals
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Schedule sx={{ color: 'warning.main' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Weekly Checks
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Fluid levels (oil, coolant, brake fluid)
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Battery condition
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Brake performance
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Air conditioning/heating system
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarMonth sx={{ color: 'info.main' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Monthly Service
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Professional inspection
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Tire rotation and alignment
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Filter replacements (air, cabin, oil)
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Suspension and steering check
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Build sx={{ color: 'error.main' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Seasonal Maintenance
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Winter: Battery test, antifreeze check
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Summer: A/C service, coolant system
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      All-season tire changeover
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Wiper blade replacement
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Best Practices */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
            Best Practices for Fleet Hosts
          </Typography>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <EventAvailable sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Schedule Regular Service
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Book maintenance appointments in advance during low-booking periods
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Description sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Keep Service Records
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Maintain detailed logs of all maintenance and repairs
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Speed sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Monitor Performance
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Track fuel efficiency and unusual noises or behaviors
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <PersonAdd sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Build Trusted Relationships
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Work with reliable mechanics and service centers
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Quick Tips */}
          <Alert severity="success" sx={{ mb: 2 }}>
            <AlertTitle>Pro Tips for Avoiding Substitutions</AlertTitle>
            <Typography variant="body2" component="div">
              <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                <li>Always inspect vehicles before accepting new bookings</li>
                <li>Block your calendar during scheduled maintenance</li>
                <li>Keep emergency contact numbers for roadside assistance</li>
                <li>Communicate proactively with guests about vehicle condition</li>
                <li>Have a backup vehicle plan for emergency situations</li>
              </Box>
            </Typography>
          </Alert>

          {/* Resources */}
          <Card variant="outlined" sx={{ backgroundColor: '#F5F5F5' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Additional Resources
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<VideoLibrary />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Watch: Complete Vehicle Maintenance Guide (12 min)
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<MenuBook />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Read: Monthly Maintenance Checklist PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ContactPhone />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Contact: Recommended Service Partners
                </Button>
              </Box>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVehicleMaintenanceDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => {
              alert('Downloading Vehicle Maintenance Checklist PDF...');
            }}
          >
            Download Checklist
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dispute Resolution Guide Dialog */}
      <Dialog
        open={disputeResolutionDialogOpen}
        onClose={() => setDisputeResolutionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="span">
              Dispute Resolution Guide
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Proactive Dispute Management</AlertTitle>
            Effective dispute resolution protects your reputation, maintains guest satisfaction, and prevents booking cancellations. Handle disputes professionally and promptly.
          </Alert>

          {/* Common Dispute Types */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            Common Dispute Types & Solutions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <DirectionsCar sx={{ color: 'error.main' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Vehicle Condition Disputes
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Guest claims vehicle doesn't match listing or has undisclosed issues
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 1 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      <strong>Prevention:</strong> Update photos regularly, document vehicle condition before each booking
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      <strong>Resolution:</strong> Provide dated photos, offer partial refund or upgrade if genuine issue
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      <strong>Timeline:</strong> Respond within 2 hours, resolve within 24 hours
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Schedule sx={{ color: 'warning.main' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Pickup/Return Time Disputes
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Disagreements about pickup delays, early returns, or late fees
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 1 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      <strong>Prevention:</strong> Clear communication, automated reminders, flexible grace periods
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      <strong>Resolution:</strong> Review timestamps, waive first-time late fees, offer makeup time
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      <strong>Timeline:</strong> Respond within 4 hours, resolve within 48 hours
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Payments sx={{ color: 'success.main' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Pricing & Billing Disputes
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Guest questions charges, fees, or damage deposits
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 1 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      <strong>Prevention:</strong> Transparent pricing breakdown, detailed damage documentation
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      <strong>Resolution:</strong> Provide itemized receipts, photos of damage, offer payment plans
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      <strong>Timeline:</strong> Respond within 6 hours, resolve within 72 hours
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Security sx={{ color: 'info.main' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Safety & Security Concerns
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Guest reports safety issues, harassment, or privacy violations
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 1 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      <strong>Prevention:</strong> Professional communication, respect boundaries, maintain vehicle safety
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      <strong>Resolution:</strong> Immediate apology, escalate to support team, offer full refund if warranted
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      <strong>Timeline:</strong> Respond immediately, escalate within 1 hour
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Resolution Steps */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
            5-Step Dispute Resolution Process
          </Typography>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 1,
                      fontWeight: 700,
                      fontSize: '1.25rem'
                    }}>
                      1
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Acknowledge Quickly
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Respond within 2 hours to show you care
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 1,
                      fontWeight: 700,
                      fontSize: '1.25rem'
                    }}>
                      2
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Listen & Empathize
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Understand guest perspective before responding
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 1,
                      fontWeight: 700,
                      fontSize: '1.25rem'
                    }}>
                      3
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Gather Evidence
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Collect photos, messages, and documentation
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 1,
                      fontWeight: 700,
                      fontSize: '1.25rem'
                    }}>
                      4
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Propose Solutions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Offer 2-3 fair resolution options
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 1,
                      fontWeight: 700,
                      fontSize: '1.25rem'
                    }}>
                      5
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Follow Up
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Confirm resolution and maintain relationship
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'warning.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 1,
                      fontWeight: 700,
                      fontSize: '0.875rem'
                    }}>
                      ⚠️
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Escalate if Needed
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contact PolarRide support for complex disputes
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Communication Templates */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
            Response Templates
          </Typography>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight={600}>Initial Acknowledgment Template</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  "Hi [Guest Name], thank you for bringing this to my attention. I understand your concern about [issue] and I'm committed to resolving this quickly. I'm currently reviewing the details and will have a solution for you within [timeframe]. Your satisfaction is my priority."
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight={600}>Apology & Resolution Template</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  "I sincerely apologize for [specific issue]. This doesn't reflect the experience I aim to provide. To make this right, I'd like to offer you [solution options]. Please let me know which works best for you, or if you'd prefer a different resolution."
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight={600}>Clarification Request Template</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  "Thank you for your message. To help me resolve this properly, could you please provide [specific information needed]? This will help me understand the situation better and find the best solution for you. I appreciate your patience."
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>

          {/* Best Practices */}
          <Alert severity="success" sx={{ mt: 3, mb: 2 }}>
            <AlertTitle>Best Practices for Dispute Prevention</AlertTitle>
            <Typography variant="body2" component="div">
              <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                <li>Set clear expectations in your listing and welcome message</li>
                <li>Document vehicle condition with photos before and after each trip</li>
                <li>Respond to guest messages within 2 hours during bookings</li>
                <li>Be proactive - address potential issues before they escalate</li>
                <li>Keep all communication on the platform for record-keeping</li>
                <li>Stay professional and courteous even when frustrated</li>
                <li>Know when to involve PolarRide support team</li>
              </Box>
            </Typography>
          </Alert>

          {/* Resources */}
          <Card variant="outlined" sx={{ backgroundColor: '#F5F5F5' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Additional Resources
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<VideoLibrary />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Watch: Conflict Resolution Masterclass (18 min)
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<MenuBook />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Read: Complete Dispute Resolution Handbook
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SupportAgent />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Contact: PolarRide Support Team
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Policy />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Review: Host Protection Policies
                </Button>
              </Box>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisputeResolutionDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => {
              alert('Downloading Dispute Resolution Guide PDF...');
            }}
          >
            Download Guide
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Documentation Guide Dialog */}
      <Dialog
        open={viewDocDialogOpen}
        onClose={() => setViewDocDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedDoc?.icon}
            <Typography variant="h6" component="span">
              {selectedDoc?.title}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>{selectedDoc?.category?.toUpperCase()}</AlertTitle>
            {selectedDoc?.description}
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Document Information
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Category:
                </Typography>
                <Chip
                  label={selectedDoc?.category}
                  size="small"
                  sx={{ mt: 0.5, textTransform: 'capitalize' }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Last Updated:
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {selectedDoc?.lastUpdated ? new Date(selectedDoc.lastUpdated).toLocaleDateString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Type:
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {selectedDoc?.isExternal ? 'External Link' : 'Internal Guide'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  URL:
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {selectedDoc?.url || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Guide Preview
            </Typography>
            <Paper variant="outlined" sx={{ p: 3, mt: 2, backgroundColor: '#f5f5f5' }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                This is a preview of the documentation guide. The full content would be loaded from the URL or stored content.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Complete step-by-step instructions<br />
                • Visual aids and screenshots<br />
                • Best practices and tips<br />
                • FAQ section<br />
                • Additional resources
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDocDialogOpen(false)}>
            Close
          </Button>
          {selectedDoc?.url && (
            <Button
              variant="contained"
              startIcon={<OpenInNew />}
              onClick={() => {
                if (selectedDoc.url) {
                  window.open(selectedDoc.url, selectedDoc.isExternal ? '_blank' : '_self');
                }
              }}
            >
              Open Full Guide
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Documentation Guide Dialog */}
      <Dialog
        open={editDocDialogOpen}
        onClose={() => setEditDocDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit />
            <Typography variant="h6" component="span">
              Edit Documentation: {selectedDoc?.title}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Edit Mode</AlertTitle>
            You are editing the documentation guide. Changes will be saved to the system.
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                defaultValue={selectedDoc?.title}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                defaultValue={selectedDoc?.description}
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  defaultValue={selectedDoc?.category || 'guide'}
                  label="Category"
                >
                  <MenuItem value="guide">Guide</MenuItem>
                  <MenuItem value="requirement">Requirement</MenuItem>
                  <MenuItem value="policy">Policy</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    defaultChecked={selectedDoc?.isExternal}
                  />
                }
                label="External Link"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL / Path"
                defaultValue={selectedDoc?.url}
                variant="outlined"
                helperText="Internal path (e.g., /docs/guide) or external URL (e.g., https://...)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content"
                variant="outlined"
                multiline
                rows={8}
                placeholder="Enter the full guide content here in Markdown format..."
                helperText="Supports Markdown formatting"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDocDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveAlt />}
            onClick={() => {
              // TODO: Implement save functionality
              alert('Documentation saved successfully!');
              setEditDocDialogOpen(false);
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Documentation Dialog */}
      <Dialog
        open={addDocDialogOpen}
        onClose={() => setAddDocDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Add />
            <Typography variant="h6" component="span">
              Add New Documentation
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Create New Documentation</AlertTitle>
            Fill in the details below to create a new documentation guide for hosts.
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                placeholder="e.g., How to Set Competitive Pricing"
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                placeholder="Brief description of what this guide covers..."
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  defaultValue="guide"
                  label="Category"
                >
                  <MenuItem value="guide">Guide</MenuItem>
                  <MenuItem value="requirement">Requirement</MenuItem>
                  <MenuItem value="policy">Policy</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch defaultChecked={false} />
                }
                label="External Link"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL / Path"
                placeholder="e.g., /docs/pricing-guide or https://..."
                variant="outlined"
                helperText="Internal path (e.g., /docs/guide) or external URL (e.g., https://...)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content"
                variant="outlined"
                multiline
                rows={8}
                placeholder="Enter the full guide content here in Markdown format...

# Example Guide Structure

## Introduction
Brief overview of the topic

## Step-by-Step Instructions
1. First step
2. Second step
3. Third step

## Best Practices
- Tip 1
- Tip 2

## FAQ
Common questions and answers"
                helperText="Supports Markdown formatting"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDocDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveAlt />}
            onClick={() => {
              // TODO: Implement save functionality
              alert('New documentation created successfully!');
              setAddDocDialogOpen(false);
            }}
          >
            Create Documentation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Training Module Dialog */}
      <Dialog open={createModuleDialogOpen} onClose={() => setCreateModuleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Training Module</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a new interactive training module to help hosts improve their skills and knowledge.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Module Title"
                value={newModule.title}
                onChange={(e) => setNewModule({ ...newModule, title: e.target.value })}
                placeholder="e.g., Advanced Host Strategies"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={newModule.description}
                onChange={(e) => setNewModule({ ...newModule, description: e.target.value })}
                placeholder="Brief description of the module and learning objectives..."
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration"
                value={newModule.duration}
                onChange={(e) => setNewModule({ ...newModule, duration: e.target.value })}
                placeholder="e.g., 30 min"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Difficulty Level</InputLabel>
                <Select
                  value={newModule.difficulty}
                  onChange={(e) => setNewModule({ ...newModule, difficulty: e.target.value as 'Beginner' | 'Intermediate' | 'Advanced' })}
                  label="Difficulty Level"
                >
                  <MenuItem value="Beginner">
                    <Chip label="Beginner" size="small" color="success" />
                  </MenuItem>
                  <MenuItem value="Intermediate">
                    <Chip label="Intermediate" size="small" color="warning" />
                  </MenuItem>
                  <MenuItem value="Advanced">
                    <Chip label="Advanced" size="small" color="error" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newModule.category}
                  onChange={(e) => setNewModule({ ...newModule, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="Onboarding">Onboarding</MenuItem>
                  <MenuItem value="Optimization">Optimization</MenuItem>
                  <MenuItem value="Safety">Safety</MenuItem>
                  <MenuItem value="Compliance">Compliance</MenuItem>
                  <MenuItem value="Growth">Growth</MenuItem>
                  <MenuItem value="Customer Service">Customer Service</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            <AlertTitle>Module Guidelines</AlertTitle>
            <Typography variant="body2">
              • Keep modules focused on specific skills or topics<br />
              • Include clear learning objectives<br />
              • Estimate duration accurately for better planning<br />
              • Choose appropriate difficulty level for target audience
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateModuleDialogOpen(false);
            setNewModule({
              title: '',
              description: '',
              duration: '',
              category: 'Onboarding',
              difficulty: 'Beginner',
              completionRate: 0
            });
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              // Validate required fields
              if (!newModule.title || !newModule.description || !newModule.duration) {
                alert('Please fill in all required fields');
                return;
              }

              // Create new module with unique ID
              const moduleToAdd: TrainingModule = {
                id: `module-${Date.now()}`,
                title: newModule.title,
                description: newModule.description,
                duration: newModule.duration,
                category: newModule.category,
                difficulty: newModule.difficulty,
                completionRate: 0
              };

              // Add to the list
              setTrainingModulesList(prev => [moduleToAdd, ...prev]);

              // Show success message
              alert('Training module created successfully!');

              // Reset form and close dialog
              setNewModule({
                title: '',
                description: '',
                duration: '',
                category: 'Onboarding',
                difficulty: 'Beginner',
                completionRate: 0
              });
              setCreateModuleDialogOpen(false);
            }}
          >
            Create Module
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDuplicateCampaign}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate Campaign</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportReport}>
          <ListItemIcon>
            <FileDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Report</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleArchiveCampaign}>
          <ListItemIcon>
            <Archive fontSize="small" />
          </ListItemIcon>
          <ListItemText>Archive Campaign</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteCampaign} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Campaign</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default HostEducationNudgesPage;
